import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Home, Users, Bed, Bath, Square, MapPin, Play, X, ChevronLeft, ChevronRight,
  Instagram, Facebook, Linkedin, Wind, Heart, ExternalLink, Star,
  MessageCircle, Send, Phone, Mail, Globe, UserPlus, Newspaper, Volume2, Link2,
  ChevronDown, Search, HelpCircle, Share2, Grid3X3, Check, Zap, Sparkles, Sun, ShieldCheck, Copy,
  Eye, Calendar, Cloud, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, CloudFog, TrendingUp,
  BarChart3, LayoutGrid, Droplets, Thermometer, ArrowUp, ArrowDown, Flag,
  Clock, LogIn, LogOut, PawPrint, Car, UserX
} from "lucide-react";
import { API, BACKEND_URL, useAuth } from "../App";
import { getPreset, boxTint } from "../data/themePresets";
import { CityGuide } from "../components/CityGuide";
import { GoogleReviewsBox } from "../components/GoogleReviewsBox";
import HouseVoicesReviews from "../components/HouseVoicesReviews";
import { toast } from "sonner";
import ReviewsTab from "../components/ReviewsTab";
import LanguageSelector from "../components/LanguageSelector";
import PropertyInstallModal from "../components/PropertyInstallModal";
// PushOptInSheet removed 2026-02 — push permission is now requested only
// as the FINAL step of a successful install (see PropertyInstallModal).
import { initOneSignalForProperty } from "../lib/onesignal";
import { useLanguage } from "../contexts/LanguageContext";
import { HostVerifiedShield } from "../components/HostVerifiedShield";
import { AMENITIES_DATA } from "../data/amenitiesData";
import AmenitiesTreemap from "../components/AmenitiesTreemap";
import ReviewsCardStack from "../components/ReviewsCardStack";
import PropertyDescription from "../components/PropertyDescription";
import PublicAvailability from "../components/PublicAvailability";
import BlogNewsBox from "../components/BlogNewsBox";
import EventsTicker from "../components/EventsTicker";
import PropertyPOIList from "../components/PropertyPOIList";
import SectionHeader from "../components/SectionHeader";
import { formatHostSeniority } from "../utils/hostSeniority";

/* ─── Amenities Landing Accordion ─── */
const AmenitiesLandingAccordion = ({ amenityIds, theme }) => {
  const { t } = useLanguage();
  const [openCats, setOpenCats] = useState({});
  const [search, setSearch] = useState("");

  // Group selected amenities by category
  const grouped = useMemo(() => {
    const allItems = new Map();
    AMENITIES_DATA.forEach((cat) => {
      cat.items.forEach((item) => allItems.set(item.id, { ...item, catId: cat.id, catLabel: cat.label, catEmoji: cat.emoji }));
    });

    const cats = {};
    amenityIds.forEach((id) => {
      const item = allItems.get(id);
      if (!item) return;
      if (!cats[item.catId]) cats[item.catId] = { id: item.catId, label: item.catLabel, emoji: item.catEmoji, items: [] };
      cats[item.catId].items.push(item);
    });
    return Object.values(cats);
  }, [amenityIds]);

  // Filter by search
  const filtered = useMemo(() => {
    if (!search.trim()) return grouped;
    const q = search.toLowerCase();
    return grouped.map((cat) => ({
      ...cat,
      items: cat.items.filter((i) => i.label.toLowerCase().includes(q) || (i.desc && i.desc.toLowerCase().includes(q))),
    })).filter((cat) => cat.items.length > 0);
  }, [grouped, search]);

  const toggleCat = (catId) => setOpenCats((p) => ({ ...p, [catId]: !p[catId] }));

  return (
    <div className="max-w-2xl mx-auto" data-testid="amenities-accordion">
      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: theme.sub }} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("landing.search_service")}
          className="w-full rounded-xl pl-10 pr-10 py-2.5 text-sm font-medium focus:outline-none focus:ring-2"
          style={{ background: theme.card, border: `1px solid ${theme.border}`, color: theme.text, "--tw-ring-color": theme.border }}
          data-testid="amenities-landing-search"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: theme.border }}>
            <X className="w-3 h-3" style={{ color: theme.sub }} />
          </button>
        )}
      </div>

      {/* Category Accordions */}
      <div className="space-y-2">
        {filtered.map((cat) => {
          const isOpen = openCats[cat.id] || !!search.trim();
          return (
            <div key={cat.id} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${theme.border}` }} data-testid={`landing-cat-${cat.id}`}>
              <button
                onClick={() => toggleCat(cat.id)}
                className="w-full flex items-center gap-3 px-4 py-3 transition-colors"
                style={{ background: isOpen ? theme.card : "transparent" }}
                data-testid={`landing-cat-toggle-${cat.id}`}
              >
                <span className="text-base">{cat.emoji}</span>
                <span className="flex-1 text-left text-sm font-bold" style={{ color: theme.text }}>{cat.label}</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: theme.border, color: theme.sub }}>
                  {cat.items.length}
                </span>
                <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                  <ChevronDown className="w-4 h-4" style={{ color: theme.sub }} />
                </motion.div>
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {cat.items.map((item) => (
                        <div key={item.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg" style={{ background: theme.bg }} data-testid={`landing-amenity-${item.id}`}>
                          <span className="text-sm">{item.emoji}</span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium" style={{ color: theme.text }}>{item.label}</p>
                            {item.desc && <p className="text-[10px]" style={{ color: theme.sub }}>{item.desc}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {filtered.length === 0 && search.trim() && (
          <div className="text-center py-8">
            <Search className="w-8 h-8 mx-auto mb-2" style={{ color: theme.border }} />
            <p className="text-sm font-medium" style={{ color: theme.sub }}>Nessun risultato per "{search}"</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Color theming was confusing for hosts (mismatched contrast, "white text on light bg"
// scenarios, broken brand consistency on shared cards). We now force every landing page
// to render on a clean white background with dark text — regardless of `theme_color`
// stored on legacy properties. The host editor no longer exposes the picker.
const FORCED_THEME = { bg: "#FFFFFF", card: "rgba(255,255,255,0.95)", text: "#222222", sub: "#717171", accent: "#222222", border: "#EBEBEB", divider: "#DDDDDD" };
const THEME_MAP = new Proxy({}, { get: () => FORCED_THEME });

const OTA_ICONS = {
  airbnb:  { name: "Airbnb",       icon: null, logo: "https://customer-assets.emergentagent.com/job_hostlink-preview/artifacts/zv3d3xik_airbnb.jpg", bg: "#FF385C", fg: "#FFFFFF" },
  booking: { name: "Booking.com",  icon: null, logo: "https://customer-assets.emergentagent.com/job_hostlink-preview/artifacts/3py0iudp_booking-com-logo-png_seeklogo-474475.png", bg: "#003580", fg: "#FFFFFF" },
  vrbo:    { name: "Vrbo",         icon: null, logo: "https://customer-assets.emergentagent.com/job_hostlink-preview/artifacts/wx8jnk5a_unnamed-4.png", bg: "#0A2D6E", fg: "#FFFFFF" },
  custom:  { name: "Prenota Diretto", icon: "→", logo: null, bg: "#111111", fg: "#FFFFFF", dynamic: true }
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06, ease: [0.25, 0.46, 0.45, 0.94] } })
};

const getDeviceHash = () => {
  const nav = navigator;
  const raw = `${nav.userAgent}|${nav.language}|${screen.width}x${screen.height}|${nav.hardwareConcurrency || 0}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) { hash = ((hash << 5) - hash) + raw.charCodeAt(i); hash |= 0; }
  return `dh_${Math.abs(hash).toString(36)}`;
};

const Divider = ({ theme, thick = false }) => <div className="my-5 md:my-9" style={{ height: thick ? 2 : 1, background: theme.divider }} />;

/* ═══════════════════════════════════════════════════════════════
   NEW TECH COMPONENTS — Cover Flow, Insight Cards, Magnetic Button
   ═══════════════════════════════════════════════════════════════ */

/* ─── Helpers ─── */
const resolveMediaUrl = (m) => (m && m.startsWith && m.startsWith("http")) ? m : `${API}/files/${m}`;
const getYoutubeId = (url) => {
  if (!url) return null;
  // Regex tollerante: supporta watch?v= con parametri extra (?si=..., ?feature=share, &t=1m),
  // youtu.be/, embed/, v/, shorts/ e livestream (live/).
  const m = String(url).match(/(?:youtube\.com\/(?:watch\?(?:.*?&)?v=|embed\/|v\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

/* ─── 3D Cover Flow Gallery (click-to-focus + swipe overlay) ─── */
const CoverFlow = ({ slides, currentIndex, setCurrentIndex, onOpen, accent }) => {
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  const dragMovedRef = useRef(false);
  const [autoplayPaused, setAutoplayPaused] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const goPrev = () => setCurrentIndex(Math.max(0, currentIndex - 1));
  const goNext = () => setCurrentIndex(Math.min(slides.length - 1, currentIndex + 1));

  // ─── Autoplay: avanza una slide ogni 3 secondi (loop, in pausa su interazione utente) ───
  useEffect(() => {
    if (autoplayPaused || slides.length <= 1) return;
    const id = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 3000);
    return () => clearInterval(id);
  }, [autoplayPaused, slides.length, setCurrentIndex]);

  const pauseAutoplay = () => {
    setAutoplayPaused(true);
  };

  const handlePanEnd = (_, info) => {
    pauseAutoplay();
    const dx = info.offset.x;
    const vx = info.velocity.x;
    if (dx < -40 || vx < -300) goNext();
    else if (dx > 40 || vx > 300) goPrev();
    setTimeout(() => { dragMovedRef.current = false; }, 50);
  };
  const handlePan = (_, info) => {
    if (Math.abs(info.offset.x) > 6) dragMovedRef.current = true;
  };

  return (
    <div ref={containerRef} className="relative w-full overflow-hidden select-none" style={{ height: "62vh", maxHeight: 560, minHeight: 320, perspective: "1400px" }} data-testid="hero-gallery">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 60%, rgba(255,56,92,0.05) 0%, transparent 60%)" }} />

      <div className="absolute inset-0 flex items-center justify-center" style={{ transformStyle: "preserve-3d" }}>
        {slides.map((slide, i) => {
          const offset = i - currentIndex;
          const abs = Math.abs(offset);
          if (abs > 3) return null;

          const x = offset * (isMobile ? 130 : 220);
          const rotateY = -offset * 32;
          const scale = abs === 0 ? 1 : abs === 1 ? 0.78 : 0.6;
          const opacity = abs === 0 ? 1 : abs === 1 ? 0.6 : 0.25;
          const isCenter = abs === 0;
          const isVideo = slide.type === "video";

          return (
            <motion.div
              key={`${slide.src}-${i}`}
              initial={false}
              animate={{ x, rotateY, scale, opacity, zIndex: 50 - abs }}
              transition={{ type: "spring", damping: 26, stiffness: 200, mass: 0.7 }}
              className="absolute rounded-2xl overflow-hidden cursor-pointer"
              style={{
                width: "clamp(240px, 56vw, 460px)",
                aspectRatio: "4/5",
                transformStyle: "preserve-3d",
                boxShadow: isCenter
                  ? "0 30px 80px -20px rgba(0,0,0,0.45), 0 8px 24px -8px rgba(0,0,0,0.25)"
                  : "0 14px 40px -12px rgba(0,0,0,0.25)"
              }}
              onClick={(e) => {
                if (dragMovedRef.current) { e.preventDefault(); return; }
                pauseAutoplay();
                if (isCenter && !isVideo) onOpen();
                else if (!isCenter) setCurrentIndex(i);
              }}
              whileHover={isCenter ? { scale: 1.015 } : { scale: scale + 0.03 }}
              data-testid={isCenter ? "cover-flow-center" : `cover-flow-side-${i}`}
              data-slide-type={slide.type}
            >
              {isVideo ? (
                <>
                  {/* Always autoplay muted loop — fills the 4:5 frame edge-to-edge (16:9 video gets cropped horizontally) */}
                  <div className="absolute inset-0 overflow-hidden bg-black">
                    <iframe
                      src={`https://www.youtube.com/embed/${slide.src}?autoplay=1&mute=1&loop=1&playlist=${slide.src}&controls=0&modestbranding=1&playsinline=1&rel=0&iv_load_policy=3&showinfo=0&disablekb=1`}
                      title="Tour video"
                      className="pointer-events-none"
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        // Cover a 4:5 container with a 16:9 video → width must be 100% * (16/9) / (4/5) = 222.22%
                        // We anchor by height (100%) and overflow horizontally; the iframe is centered so left/right are cropped equally.
                        height: "100%",
                        width: "222.22%",
                        transform: "translate(-50%, -50%)",
                        border: 0,
                      }}
                      allow="autoplay; encrypted-media; picture-in-picture"
                      loading="lazy"
                    />
                  </div>
                  {/* Video badge */}
                  {isCenter && (
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-bold backdrop-blur-md pointer-events-none z-10" style={{ background: "rgba(255,56,92,0.95)", color: "#fff" }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      TOUR VIDEO
                    </div>
                  )}
                </>
              ) : (
                <img src={slide.src} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
              )}
              {!isCenter && (
                <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0) 60%, rgba(255,255,255,0.35) 100%)" }} />
              )}
              {isCenter && !isVideo && (
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.25) 100%)" }} />
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Invisible swipe overlay (mobile only — captures swipe, delegates clicks via elementFromPoint) */}
      {isMobile && (
        <motion.div
          className="absolute inset-x-0 top-0 bottom-0"
          style={{ zIndex: 200, background: "transparent", touchAction: "pan-y" }}
          onPan={handlePan}
          onPanEnd={handlePanEnd}
          onClick={(e) => {
            if (dragMovedRef.current) return;
            pauseAutoplay();
            // Temporarily hide overlay, get element below, then handle
            const overlay = e.currentTarget;
            overlay.style.display = "none";
            const target = document.elementFromPoint(e.clientX, e.clientY);
            overlay.style.display = "";
            let el = target;
            while (el) {
              const tid = el.getAttribute && el.getAttribute("data-testid");
              if (tid === "cover-flow-center") { onOpen(); return; }
              if (tid && tid.startsWith("cover-flow-side-")) {
                const idx = parseInt(tid.replace("cover-flow-side-", ""), 10);
                if (!isNaN(idx)) setCurrentIndex(idx);
                return;
              }
              el = el.parentElement;
            }
          }}
          data-testid="cover-flow-swipe-area"
        />
      )}

      {/* Nav arrows (desktop) */}
      {!isMobile && slides.length > 1 && (
        <>
          <button onClick={() => { pauseAutoplay(); goPrev(); }} disabled={currentIndex === 0}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center shadow-md hover:scale-105 transition-all disabled:opacity-30 disabled:cursor-not-allowed z-10"
            data-testid="gallery-prev" aria-label="Foto precedente">
            <ChevronLeft className="w-5 h-5 text-zinc-800" />
          </button>
          <button onClick={() => { pauseAutoplay(); goNext(); }} disabled={currentIndex === slides.length - 1}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center shadow-md hover:scale-105 transition-all disabled:opacity-30 disabled:cursor-not-allowed z-10"
            data-testid="gallery-next" aria-label="Foto successiva">
            <ChevronRight className="w-5 h-5 text-zinc-800" />
          </button>
        </>
      )}

      {/* Indicator dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10 px-2.5 py-1.5 rounded-full backdrop-blur-md" style={{ background: "rgba(255,255,255,0.7)" }}>
          {slides.slice(0, Math.min(slides.length, 8)).map((s, i) => (
            <button key={i} onClick={() => { pauseAutoplay(); setCurrentIndex(i); }} aria-label={`Slide ${i + 1}`}
              className="rounded-full transition-all" style={{
                width: i === currentIndex ? 18 : 5, height: 5,
                background: i === currentIndex ? accent : "#999"
              }}/>
          ))}
        </div>
      )}

      {/* Counter pill (top-right) */}
      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[11px] font-bold backdrop-blur-md z-10" style={{ background: "rgba(0,0,0,0.55)", color: "#fff" }}>
        {currentIndex + 1} / {slides.length}
      </div>
    </div>
  );
};

/* ─── Animated Counter (scroll-triggered, count from 0 to value) ─── */
const AnimatedCounter = ({ value, duration = 1.2, suffix = "" }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const inViewRef = useRef(false);
  const rafRef = useRef(null);

  // Single IntersectionObserver — tracks visibility only. Does NOT start the
  // animation by itself; the run effect below reads ``inViewRef`` so we can
  // restart whenever ``value`` changes (e.g. async API hydration).
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      inViewRef.current = entry.isIntersecting;
    }, { threshold: 0.4 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Run the animation whenever value changes. If the element isn't visible
  // yet, poll briefly until it enters the viewport.
  useEffect(() => {
    const parsed = Number(value);
    const target = Number.isFinite(parsed) ? parsed : 0;

    let cancelled = false;
    const run = () => {
      const start = Date.now();
      const from = 0;
      const tick = () => {
        if (cancelled) return;
        const elapsed = (Date.now() - start) / 1000;
        const t = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        const next = Math.round(from + (target - from) * eased);
        setDisplay(Number.isFinite(next) ? next : 0);
        if (t < 1) rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    // Fast-path: target is 0 → no animation needed.
    if (target === 0) {
      setDisplay(0);
      return () => { cancelled = true; };
    }

    if (inViewRef.current) {
      run();
    } else {
      // Wait at most 8s for the IntersectionObserver to flip the flag, then
      // fall back to snapping to the final value (never leaves the user stuck at 0).
      let waited = 0;
      const wait = setInterval(() => {
        if (cancelled) { clearInterval(wait); return; }
        waited += 120;
        if (inViewRef.current || waited >= 8000) {
          clearInterval(wait);
          if (!cancelled) run();
        }
      }, 120);
      return () => { cancelled = true; clearInterval(wait); if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }
    return () => { cancelled = true; if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value, duration]);

  const safeDisplay = Number.isFinite(display) ? display : 0;
  return <span ref={ref}>{safeDisplay}{suffix}</span>;
};

/* ─── Insight Card with animated SVG mini-graph on hover ─── */
const InsightCard = ({ icon: Icon, label, value, valueSuffix = "", graphType, graphData, accent, theme, testId }) => {
  const [hovered, setHovered] = useState(false);
  const enter = () => setHovered(true);
  const leave = () => setHovered(false);

  return (
    <motion.button
      type="button"
      onPointerEnter={enter}
      onPointerLeave={leave}
      onFocus={enter}
      onBlur={leave}
      onClick={() => setHovered((h) => !h)}
      whileTap={{ scale: 0.97 }}
      className="relative rounded-3xl p-5 md:p-6 text-left w-full overflow-hidden transition-all"
      style={{
        background: hovered ? "#FFFFFF" : "#FAFAFB",
        border: `1px solid ${hovered ? accent + "30" : theme.border}`,
        boxShadow: hovered ? `0 20px 60px -20px ${accent}25, 0 8px 20px -8px rgba(0,0,0,0.08)` : "0 1px 2px rgba(0,0,0,0.03)"
      }}
      data-testid={testId}
    >
      {/* Background accent blob on hover */}
      <motion.div
        className="absolute -top-8 -right-8 w-28 h-28 rounded-full pointer-events-none"
        animate={{ opacity: hovered ? 0.18 : 0, scale: hovered ? 1.3 : 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        style={{ background: accent }}
      />      <div className="relative flex items-center justify-between mb-3">
        <motion.div
          animate={{ rotate: hovered ? -8 : 0, scale: hovered ? 1.05 : 1 }}
          transition={{ type: "spring", damping: 15, stiffness: 200 }}
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: accent + "15" }}
        >
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </motion.div>
        <ChevronRight className="w-3.5 h-3.5 transition-transform" style={{ color: theme.sub, transform: hovered ? "translateX(2px)" : "" }} />
      </div>

      <p className="relative text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5" style={{ color: theme.sub }}>{label}</p>

      <div className="relative h-12 flex items-end">
        <AnimatePresence mode="wait" initial={false}>
          {!hovered ? (
            <motion.span
              key="value"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="text-3xl md:text-4xl font-bold tracking-tight"
              style={{ color: theme.text }}
            >
              {value}{valueSuffix && <span className="text-base font-medium" style={{ color: theme.sub }}>{valueSuffix}</span>}
            </motion.span>
          ) : (
            <motion.div
              key="graph"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="w-full"
            >
              {graphType === "bars" && <BarGraph data={graphData} accent={accent} />}
              {graphType === "ring" && <RingGraph value={graphData} accent={accent} theme={theme} />}
              {graphType === "stars" && <StarsGraph value={graphData} accent={accent} theme={theme} />}
              {graphType === "wave" && <WaveGraph data={graphData} accent={accent} />}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.button>
  );
};

/* ─── Mini SVG graphs ─── */
const BarGraph = ({ data, accent }) => (
  <svg viewBox="0 0 100 40" className="w-full h-12">
    {data.map((v, i) => (
      <motion.rect
        key={i}
        x={i * 18 + 2}
        width={12}
        rx={2}
        initial={{ y: 40, height: 0 }}
        animate={{ y: 40 - v * 0.4, height: v * 0.4 }}
        transition={{ delay: i * 0.06, type: "spring", damping: 14, stiffness: 200 }}
        fill={accent}
        opacity={0.5 + i * 0.08}
      />
    ))}
  </svg>
);

const RingGraph = ({ value, accent, theme }) => {
  const pct = Math.min(Math.max(value, 0), 100);
  const circumference = 2 * Math.PI * 18;
  return (
    <div className="flex items-center gap-2.5">
      <svg viewBox="0 0 44 44" className="w-12 h-12 -rotate-90">
        <circle cx="22" cy="22" r="18" fill="none" stroke={theme.border} strokeWidth="4" />
        <motion.circle
          cx="22" cy="22" r="18" fill="none" stroke={accent} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - (pct / 100) * circumference }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </svg>
      <span className="text-2xl font-bold" style={{ color: theme.text }}><AnimatedCounter value={pct} duration={0.8} suffix="%" /></span>
    </div>
  );
};

const StarsGraph = ({ value, accent, theme }) => {
  const full = Math.floor(value);
  return (
    <div className="flex items-center gap-0.5">
      {[0, 1, 2, 3, 4].map(i => (
        <motion.div
          key={i}
          initial={{ scale: 0, rotate: -90 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: i * 0.08, type: "spring", damping: 12 }}
        >
          <Star className="w-5 h-5" fill={i < full ? accent : "none"} style={{ color: i < full ? accent : theme.sub }} strokeWidth={1.5} />
        </motion.div>
      ))}
      <span className="ml-2 text-base font-bold" style={{ color: theme.text }}>{value.toFixed(1)}</span>
    </div>
  );
};

const WaveGraph = ({ data, accent }) => {
  const pts = data.map((v, i) => `${i * (100 / (data.length - 1))},${30 - v * 0.5}`).join(" ");
  return (
    <svg viewBox="0 0 100 40" className="w-full h-12">
      <motion.polyline
        points={pts}
        fill="none"
        stroke={accent}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 1.1, ease: "easeOut" }}
      />
      {data.map((v, i) => (
        <motion.circle
          key={i}
          cx={i * (100 / (data.length - 1))}
          cy={30 - v * 0.5}
          r={1.6}
          fill={accent}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.4 + i * 0.05 }}
        />
      ))}
    </svg>
  );
};

/* ─── Magnetic Button (follows cursor slightly) ─── */
const MagneticButton = ({ children, className, style, onClick, testId, intensity = 0.25, ...rest }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - (r.left + r.width / 2)) * intensity;
    const y = (e.clientY - (r.top + r.height / 2)) * intensity;
    setPos({ x, y });
  };
  const handleLeave = () => setPos({ x: 0, y: 0 });

  return (
    <motion.button
      ref={ref}
      type="button"
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
      onClick={onClick}
      animate={{ x: pos.x, y: pos.y }}
      transition={{ type: "spring", damping: 14, stiffness: 220, mass: 0.4 }}
      whileTap={{ scale: 0.96 }}
      className={className}
      style={style}
      data-testid={testId}
      {...rest}
    >
      <motion.span
        animate={{ x: pos.x * 0.3, y: pos.y * 0.3 }}
        transition={{ type: "spring", damping: 14, stiffness: 220 }}
        className="inline-flex items-center gap-2"
        style={{ pointerEvents: "none" }}
      >
        {children}
      </motion.span>
    </motion.button>
  );
};

/* ─── Stats Pill Row (animated counter pills) ─── */
// ═══════════════════════════════════════
// MODERN ANIMATED KPI CARDS — Occupancy, Total Views, 7-day Weather
// ═══════════════════════════════════════

// Map Open-Meteo WMO codes to icon + label
const WEATHER_ICON = (code) => {
  if (code === 0) return { Icon: Sun, label: "Sereno", color: "#F59E0B" };
  if ([1, 2].includes(code)) return { Icon: Sun, label: "Poco nuvoloso", color: "#F59E0B" };
  if (code === 3) return { Icon: Cloud, label: "Nuvoloso", color: "#94A3B8" };
  if ([45, 48].includes(code)) return { Icon: CloudFog, label: "Nebbia", color: "#94A3B8" };
  if ([51, 53, 55, 56, 57].includes(code)) return { Icon: CloudDrizzle, label: "Pioviggine", color: "#60A5FA" };
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return { Icon: CloudRain, label: "Pioggia", color: "#3B82F6" };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { Icon: CloudSnow, label: "Neve", color: "#A5B4FC" };
  if ([95, 96, 99].includes(code)) return { Icon: CloudLightning, label: "Temporale", color: "#7C3AED" };
  return { Icon: Cloud, label: "—", color: "#94A3B8" };
};

const DAY_TRANSLATION_KEYS = ["day.sun_short", "day.mon_short", "day.tue_short", "day.wed_short", "day.thu_short", "day.fri_short", "day.sat_short"];

// Dynamic sky palette per weather condition — gives the widget its "wow" mood.
const WEATHER_THEME = (code) => {
  if ([0, 1, 2].includes(code)) return { bg: "linear-gradient(145deg,#E0F2FE 0%,#FEF3C7 100%)", border: "#FDE68A", accent: "#F59E0B", sub: "#B45309", chip: "rgba(245,158,11,0.12)" };
  if ([3, 45, 48].includes(code)) return { bg: "linear-gradient(145deg,#F1F5F9 0%,#E2E8F0 100%)", border: "#CBD5E1", accent: "#64748B", sub: "#475569", chip: "rgba(100,116,139,0.12)" };
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return { bg: "linear-gradient(145deg,#DBEAFE 0%,#E0F2FE 100%)", border: "#BFDBFE", accent: "#3B82F6", sub: "#1D4ED8", chip: "rgba(59,130,246,0.12)" };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { bg: "linear-gradient(145deg,#EEF2FF 0%,#E0E7FF 100%)", border: "#C7D2FE", accent: "#6366F1", sub: "#4338CA", chip: "rgba(99,102,241,0.12)" };
  if ([95, 96, 99].includes(code)) return { bg: "linear-gradient(145deg,#EDE9FE 0%,#F3E8FF 100%)", border: "#DDD6FE", accent: "#7C3AED", sub: "#6D28D9", chip: "rgba(124,58,237,0.12)" };
  return { bg: "linear-gradient(145deg,#F1F5F9,#E2E8F0)", border: "#CBD5E1", accent: "#64748B", sub: "#475569", chip: "rgba(100,116,139,0.12)" };
};

const AnimatedWeatherIcon = ({ code, className = "w-14 h-14", color }) => {
  const { Icon } = WEATHER_ICON(code);
  const isSun = [0, 1, 2].includes(code);
  const isRain = [51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code);
  const anim = isSun ? { rotate: [0, 360] } : isRain ? { y: [0, 3, 0] } : { x: [0, 4, 0] };
  const dur = isSun ? 26 : isRain ? 1.5 : 5;
  return (
    <motion.div animate={anim} transition={{ duration: dur, repeat: Infinity, ease: isSun ? "linear" : "easeInOut" }} className="drop-shadow-sm">
      <Icon className={className} style={{ color }} strokeWidth={2} />
    </motion.div>
  );
};

const MiniStat = ({ icon: Icon, label, value, accent }) => (
  <div className="flex-1 min-w-0 rounded-2xl px-2 py-2 flex flex-col items-center gap-0.5" style={{ background: "rgba(255,255,255,0.55)" }}>
    <Icon className="w-3.5 h-3.5 mb-0.5" style={{ color: accent }} strokeWidth={2.4} />
    <span className="text-[12px] font-black tabular-nums text-zinc-900 leading-none">{value}</span>
    <span className="text-[8px] font-bold uppercase tracking-wide text-zinc-500 leading-none">{label}</span>
  </div>
);

const WeatherWeekCard = ({ weather, theme }) => {
  const { t } = useLanguage();
  const daily = weather?.daily;
  if (!daily?.time?.length) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55, delay: 0.16 }}
        className="relative overflow-hidden rounded-3xl p-5 cursor-default flex items-center justify-center min-h-[140px]"
        style={{ background: "linear-gradient(135deg, #F0FDF4, #DCFCE7)", border: "1px solid #BBF7D0" }}
        data-testid="kpi-weather"
      >
        <p className="text-xs text-emerald-700/70">{t("kpi.weather_unavailable")}</p>
      </motion.div>
    );
  }

  const code = weather.weather_code ?? daily.weather_code?.[0] ?? 0;
  const th = WEATHER_THEME(code);
  const { label } = WEATHER_ICON(code);
  const cur = Math.round(weather.temperature_2m ?? daily.temperature_2m_max?.[0] ?? 0);
  const feels = Math.round(weather.apparent_temperature ?? cur);
  const wind = Math.round(weather.wind_speed_10m ?? 0);
  const tMax = Math.round(daily.temperature_2m_max?.[0] ?? cur);
  const tMin = Math.round(daily.temperature_2m_min?.[0] ?? cur);
  const next3 = (daily.time || []).slice(1, 4);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: 0.16 }}
      whileHover={{ y: -4 }}
      className="relative overflow-hidden rounded-3xl p-5 cursor-default"
      style={{ background: th.bg, border: `1px solid ${th.border}` }}
      data-testid="kpi-weather"
    >
      {/* Animated aura */}
      <motion.div
        className="absolute -top-14 -right-10 w-40 h-40 rounded-full blur-3xl opacity-40 pointer-events-none"
        style={{ background: th.accent }}
        animate={{ x: [0, 24, 0], y: [0, 16, 0], scale: [1, 1.15, 1] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md" style={{ background: `linear-gradient(135deg, ${th.accent}, ${th.sub})` }}>
            <Cloud className="w-4 h-4 text-white" strokeWidth={2.4} />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: th.sub }}>{t("kpi.weather_now")}</span>
          <span className="ml-auto inline-flex items-center gap-1">
            <motion.span className="w-1.5 h-1.5 rounded-full" style={{ background: th.accent }}
              animate={{ opacity: [1, 0.25, 1], scale: [1, 0.85, 1] }} transition={{ duration: 1.8, repeat: Infinity }} />
            <span className="text-[8px] font-black uppercase tracking-wider" style={{ color: th.sub }}>{t("kpi.weather_live")}</span>
          </span>
        </div>

        {/* Current: big temp + animated icon */}
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-start leading-none">
              <span className="text-5xl md:text-6xl font-black tracking-tight tabular-nums text-zinc-900">
                <AnimatedCounter value={cur} duration={1.2} />
              </span>
              <span className="text-2xl font-black mt-1" style={{ color: th.accent }}>°</span>
            </div>
            <p className="text-[12px] font-bold mt-1 truncate" style={{ color: th.sub }}>{label}</p>
          </div>
          <AnimatedWeatherIcon code={code} color={th.accent} className="w-16 h-16 md:w-[72px] md:h-[72px] shrink-0" />
        </div>

        {/* Stats: feels · wind · max/min */}
        <div className="flex gap-2 mt-4">
          <MiniStat icon={Thermometer} label={t("kpi.weather_feels")} value={`${feels}°`} accent={th.accent} />
          <MiniStat icon={Wind} label={t("kpi.weather_wind")} value={`${wind}`} accent={th.accent} />
          <div className="flex-1 min-w-0 rounded-2xl px-2 py-2 flex flex-col items-center justify-center gap-0.5" style={{ background: "rgba(255,255,255,0.55)" }}>
            <span className="inline-flex items-center gap-1 text-[12px] font-black tabular-nums text-zinc-900 leading-none"><ArrowUp className="w-3 h-3" style={{ color: th.accent }} strokeWidth={3} />{tMax}°</span>
            <span className="inline-flex items-center gap-1 text-[11px] font-bold tabular-nums text-zinc-500 leading-none"><ArrowDown className="w-3 h-3 text-zinc-400" strokeWidth={3} />{tMin}°</span>
          </div>
        </div>

        {/* Next 3 days */}
        {next3.length > 0 && (
          <div className="mt-3 pt-3 border-t flex items-stretch gap-1.5" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
            {next3.map((iso, k) => {
              const i = k + 1;
              const c = daily.weather_code?.[i] ?? 0;
              const mx = Math.round(daily.temperature_2m_max?.[i] ?? 0);
              const mn = Math.round(daily.temperature_2m_min?.[i] ?? 0);
              const dd = new Date(iso);
              const dlabel = t(DAY_TRANSLATION_KEYS[dd.getDay()]);
              const meta = WEATHER_ICON(c);
              return (
                <div key={iso} className="flex-1 flex flex-col items-center gap-1 py-1 rounded-xl" style={{ background: "rgba(255,255,255,0.4)" }}>
                  <span className="text-[9px] font-bold uppercase" style={{ color: th.sub }}>{dlabel}</span>
                  <meta.Icon className="w-4 h-4" style={{ color: meta.color }} strokeWidth={2.2} />
                  <div className="flex items-baseline gap-1 leading-none">
                    <span className="text-[11px] font-black tabular-nums text-zinc-900">{mx}°</span>
                    <span className="text-[9px] tabular-nums text-zinc-400">{mn}°</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Anello radiale animato → rappresenta la % di occupazione.
const RadialProgress = ({ value = 0 }) => {
  const v = Math.max(0, Math.min(100, value));
  const c1 = v >= 70 ? "#F43F5E" : v >= 30 ? "#F59E0B" : "#10B981";
  const c2 = v >= 70 ? "#FB923C" : v >= 30 ? "#FDE047" : "#5EEAD4";
  const R = 34, C = 2 * Math.PI * R;
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full">
      <defs>
        <linearGradient id="occGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
        </linearGradient>
      </defs>
      <circle cx="40" cy="40" r={R} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="7" />
      <motion.circle
        cx="40" cy="40" r={R} fill="none" stroke="url(#occGrad)" strokeWidth="7" strokeLinecap="round"
        transform="rotate(-90 40 40)" strokeDasharray={C}
        initial={{ strokeDashoffset: C }}
        animate={{ strokeDashoffset: C - (C * v) / 100 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      />
    </svg>
  );
};

// Sparkline ad area "in salita" → rappresenta la crescita delle visite.
const RisingSparkline = ({ color = "#10B981" }) => {
  const pts = [[0, 27], [17, 23], [33, 25], [50, 17], [67, 19], [84, 10], [100, 4]];
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0]} ${p[1]}`).join(" ");
  const area = `${line} L100 32 L0 32 Z`;
  return (
    <svg viewBox="0 0 100 32" preserveAspectRatio="none" className="w-full h-10 md:h-12">
      <defs>
        <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path d={area} fill="url(#sparkGrad)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.35 }} />
      <motion.path d={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2, ease: "easeOut" }} />
    </svg>
  );
};

// Desktop-only responsive font sizing: fewer digits → bigger number.
const useIsDesktop = () => {
  const [d, setD] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 768px)");
    const u = () => setD(mq.matches);
    u();
    mq.addEventListener?.("change", u);
    return () => mq.removeEventListener?.("change", u);
  }, []);
  return d;
};
const deskSizeByDigits = (n) => {
  if (n <= 1) return 132;
  if (n === 2) return 116;
  if (n === 3) return 92;
  if (n === 4) return 72;
  if (n === 5) return 58;
  return 46;
};

const OccupancyCard = ({ value, theme }) => {
  const { t } = useLanguage();
  const isDesktop = useIsDesktop();
  const safeVal = Math.max(0, Math.min(100, value || 0));
  const occDigits = String(Math.round(safeVal)).length;
  const occSize = deskSizeByDigits(occDigits);
  const grad = safeVal >= 70
    ? "from-rose-500 via-rose-400 to-orange-400"
    : safeVal >= 30
    ? "from-amber-500 via-amber-400 to-yellow-300"
    : "from-emerald-500 via-emerald-400 to-teal-300";
  // Colore barra + etichetta in base alla soglia: verde (libero) → rosso (quasi pieno)
  const level = safeVal >= 70 ? "high" : safeVal >= 30 ? "mid" : "low";
  const barGrad = level === "high"
    ? "linear-gradient(90deg, #FB7185, #F43F5E)"
    : level === "mid"
    ? "linear-gradient(90deg, #FBBF24, #F97316)"
    : "linear-gradient(90deg, #34D399, #10B981)";
  const capColor = level === "high" ? "#E11D48" : level === "mid" ? "#D97706" : "#059669";
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55 }}
      whileHover={{ y: -4 }}
      className="relative overflow-hidden rounded-3xl p-4 md:p-5 cursor-default aspect-square md:aspect-auto flex flex-col"
      style={{ background: "linear-gradient(135deg, #FFF1F2, #FEE2E2)", border: "1px solid #FECACA" }}
      data-testid="kpi-occupancy"
    >
      <motion.div
        className={`absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl bg-gradient-to-br ${grad} opacity-25`}
        animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
        transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
      />
      <div className="relative flex flex-col h-full text-center">
        <span className="text-[10px] md:text-[11px] font-bold uppercase tracking-wide text-rose-700/80 leading-none block">{t("kpi.occupancy_90")}</span>
        {/* Numero: in alto su mobile, centrato verticalmente nella card su desktop */}
        <div className="mt-2 md:mt-0 md:absolute md:inset-x-0 md:top-1/2 md:-translate-y-1/2">
          <div className="flex items-baseline justify-center leading-none">
            <span className="text-5xl font-black tracking-tighter tabular-nums text-zinc-900" style={isDesktop ? { fontSize: occSize, lineHeight: 1 } : undefined}>
              <AnimatedCounter value={safeVal} duration={1.4} />
            </span>
            <span className="text-2xl font-bold text-rose-500/70" style={isDesktop ? { fontSize: Math.round(occSize * 0.42), lineHeight: 1 } : undefined}>%</span>
          </div>
        </div>
        {/* Barra occupazione animata: colore in base alla soglia */}
        <div className="mt-auto">
          <div className="w-full h-2.5 md:h-3 rounded-full bg-black/[0.06] overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundImage: barGrad }}
              initial={{ width: 0 }}
              whileInView={{ width: `${safeVal}%` }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <p className="text-[9px] md:text-[11px] mt-2 font-semibold leading-tight break-words" style={{ color: capColor }}>
            {safeVal >= 70 ? t("kpi.occupancy_high") : safeVal >= 30 ? t("kpi.occupancy_mid") : t("kpi.occupancy_low")}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const ViewsCard = ({ value, theme }) => {
  const { t } = useLanguage();
  const isDesktop = useIsDesktop();
  const vDigits = String(Math.round(value || 0)).length;
  const vSize = deskSizeByDigits(vDigits);
  return (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay: 0.08 }}
    whileHover={{ y: -4 }}
    className="relative overflow-hidden rounded-3xl p-4 md:p-5 cursor-default aspect-square md:aspect-auto flex flex-col"
    style={{ background: "linear-gradient(135deg, #FAFAFA, #F4F4F5)", border: "1px solid #E4E4E7" }}
    data-testid="kpi-views"
  >
    <motion.div
      className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full blur-3xl bg-gradient-to-tr from-zinc-400 via-zinc-300 to-zinc-200 opacity-30"
      animate={{ scale: [1, 1.3, 1], rotate: [360, 180, 0] }}
      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
    />
    <div className="relative flex flex-col h-full text-center">
      <span className="text-[10px] md:text-[11px] font-bold uppercase tracking-wide text-zinc-500 leading-none block">{t("kpi.views")}</span>
      <div className="mt-2 md:mt-0 md:flex-1 md:flex md:items-center md:justify-center">
        <div className="flex items-baseline justify-center gap-2">
          <span className="text-4xl font-black tracking-tight tabular-nums text-zinc-900 leading-none" style={isDesktop ? { fontSize: vSize, lineHeight: 1 } : undefined}>
            <AnimatedCounter value={value || 0} duration={1.6} />
          </span>
          <TrendingUp className="w-5 h-5 md:w-8 md:h-8 text-emerald-500 shrink-0" />
        </div>
      </div>
      <div className="mt-auto">
        <RisingSparkline color="#10B981" />
        <p className="text-[9px] md:text-[10px] mt-2 font-medium text-zinc-500 truncate">{t("kpi.views_sub")}</p>
      </div>
    </div>
  </motion.div>
  );
};

// Colourful palette for stat pills — one tone per stat for visual distinction
const PILL_PALETTES = [
  { from: "#FF385C", to: "#FF7A4A", glow: "rgba(255,56,92,0.35)" },     // pink → orange (guests)
  { from: "#8B5CF6", to: "#6366F1", glow: "rgba(139,92,246,0.35)" },    // violet → indigo (rooms)
  { from: "#06B6D4", to: "#3B82F6", glow: "rgba(6,182,212,0.35)" },     // cyan → blue (bathrooms)
  { from: "#10B981", to: "#84CC16", glow: "rgba(16,185,129,0.35)" },    // emerald → lime (sqft)
];

const StatsPillRow = ({ stats, theme, accent }) => (
  <div className="grid gap-2.5 md:gap-3" style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }}>
    {stats.map((s, i) => {
      const p = PILL_PALETTES[i % PILL_PALETTES.length];
      return (
        <motion.div
          key={s.label}
          initial={{ opacity: 0, y: 16, scale: 0.9 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: i * 0.08, type: "spring", stiffness: 220, damping: 18 }}
          whileHover={{ y: -4, scale: 1.04, boxShadow: `0 10px 30px -10px ${p.glow}` }}
          className="relative flex flex-col md:flex-row items-center justify-center gap-1 md:gap-3 px-2 py-2.5 md:px-4 md:py-4 rounded-2xl cursor-default overflow-hidden min-w-0"
          style={{
            background: `linear-gradient(135deg, ${p.from}14, ${p.to}1F)`,
            border: `1px solid ${p.from}33`,
          }}
          data-testid={`stat-pill-${s.label}`}
        >
          {/* Animated gradient sheen */}
          <motion.div
            className="absolute inset-0 opacity-50"
            style={{ background: `linear-gradient(115deg, transparent 40%, ${p.from}30 50%, transparent 60%)`, backgroundSize: "200% 100%" }}
            animate={{ backgroundPosition: ["100% 0%", "-100% 0%"] }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear", delay: i * 0.5 }}
          />
          <div
            className="relative w-8 h-8 md:w-9 md:h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
            style={{ background: `linear-gradient(135deg, ${p.from}, ${p.to})` }}
          >
            <s.icon className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          {/* Numero + label compatti su mobile (sotto l'icona).
              Su desktop tornano in riga con gap normale. Min-w-0 e truncate
              evitano il taglio della label "ospiti / camere / bagni / m²". */}
          <div className="relative flex flex-col md:flex-row md:items-baseline md:gap-1.5 items-center min-w-0 leading-none">
            <span className="text-lg md:text-2xl font-black tracking-tight tabular-nums" style={{ color: theme.text }}>
              <AnimatedCounter value={s.val} duration={0.9} />
            </span>
            <span className="text-[10px] md:text-sm font-semibold mt-0.5 md:mt-0 truncate max-w-full" style={{ color: theme.sub }}>{s.label}</span>
          </div>
        </motion.div>
      );
    })}
  </div>
);

// ═══════════════════════════════════════
// STAY CONDITIONS — box unica con orari e regole (check-in/out, animali, adulti, parcheggio)
// Mostra SOLO i campi compilati dall'host. Se nessuno è compilato → non renderizza nulla.
// ═══════════════════════════════════════
const StayConditions = ({ property, theme }) => {
  const { t } = useLanguage();
  const isBool = (v) => v === true || v === false;
  const hasCheckin = !!(property.check_in_from && property.check_in_to);
  const items = [];
  if (hasCheckin) items.push({ key: "checkin", icon: LogIn, from: "#F59E0B", to: "#F97316", label: t("cond.checkin"), value: `${property.check_in_from} – ${property.check_in_to}` });
  if (property.check_out_time) items.push({ key: "checkout", icon: LogOut, from: "#0EA5E9", to: "#2563EB", label: t("cond.checkout"), value: `${t("cond.checkout_by")} ${property.check_out_time}` });
  if (isBool(property.pets_allowed)) items.push({ key: "pets", icon: PawPrint, from: "#10B981", to: "#059669", label: t("cond.pets"), bool: property.pets_allowed, yes: t("cond.pets_yes"), no: t("cond.pets_no") });
  if (isBool(property.adults_only)) items.push({ key: "adults", icon: UserX, from: "#8B5CF6", to: "#7C3AED", label: t("cond.adults_only"), bool: property.adults_only, yes: t("cond.yes"), no: t("cond.no") });
  if (isBool(property.private_parking)) items.push({ key: "parking", icon: Car, from: "#EF4444", to: "#DC2626", label: t("cond.parking"), bool: property.private_parking, yes: t("cond.yes"), no: t("cond.no") });
  if (isBool(property.ev_charging)) items.push({ key: "ev", icon: Zap, from: "#14B8A6", to: "#0D9488", label: t("cond.ev"), bool: property.ev_charging, yes: t("cond.yes"), no: t("cond.no") });
  if (items.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5 }}
      className="mt-6 md:mt-8 rounded-3xl p-5 md:p-7 border"
      style={{ background: "#FFFFFF", borderColor: "#EFEFF1", boxShadow: "0 10px 32px -20px rgba(0,0,0,.22)" }}
      data-testid="stay-conditions-box"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg,#E5372B,#C62B21)" }}>
          <Clock className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <h3 className="font-black leading-tight" style={{ color: theme.text, fontSize: 19 }}>{t("cond.title")}</h3>
          <p className="text-xs md:text-sm font-semibold" style={{ color: theme.sub }}>{t("cond.subtitle")}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((it, i) => (
          <motion.div
            key={it.key}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className="flex items-center gap-3.5 rounded-2xl p-3.5 border"
            style={{ borderColor: "#F1F1F3", background: "#FAFAFA" }}
            data-testid={`condition-${it.key}`}
          >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 shadow-sm" style={{ background: `linear-gradient(135deg, ${it.from}, ${it.to})` }}>
              <it.icon className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: theme.sub }}>{it.label}</p>
              {it.value ? (
                <p className="font-black truncate" style={{ color: theme.text, fontSize: 15.5 }}>{it.value}</p>
              ) : (
                <span className="inline-flex items-center gap-1 mt-1 px-2.5 py-0.5 rounded-full text-sm font-black"
                  style={it.bool ? { background: "#DCFCE7", color: "#15803D" } : { background: "#F4F4F5", color: "#71717A" }}>
                  {it.bool ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <X className="w-3.5 h-3.5" strokeWidth={3} />}
                  {it.bool ? it.yes : it.no}
                </span>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};
// ═══════════════════════════════════════
// CONTACT POPUP — Host direct contact (Phone / Email / WhatsApp)
// Mobile bottom-sheet, desktop centered modal
// ═══════════════════════════════════════
const ContactPopup = ({ open, onClose, property, host, t }) => {
  if (!open || !property) return null;
  const phone = (property.phone || host?.phone || "").trim();
  const email = (property.email || host?.email || "").trim();
  const whatsapp = (property.whatsapp || host?.whatsapp || "").trim();
  const whatsappClean = whatsapp.replace(/[^0-9+]/g, "");

  const channels = [
    whatsapp && {
      key: "whatsapp",
      label: "WhatsApp",
      hint: whatsapp,
      href: `https://wa.me/${whatsappClean}`,
      target: "_blank",
      bg: "linear-gradient(135deg, #25D366, #128C7E)",
      icon: (
        <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 2C6.477 2 2 6.477 2 12c0 1.997.594 3.85 1.6 5.4L2 22l4.6-1.6A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/></svg>
      ),
    },
    phone && {
      key: "phone",
      label: "Telefono",
      hint: t?.("htb.tap_to_call") || "Tocca per chiamare",
      href: `tel:${phone.replace(/\s+/g, "")}`,
      target: "_self",
      bg: "linear-gradient(135deg, #3B82F6, #1D4ED8)",
      icon: <Phone className="w-6 h-6 text-white" />,
    },
    email && {
      key: "email",
      label: "Email",
      hint: t?.("htb.tap_to_email") || "Tocca per scrivere",
      action: () => { window.location.href = `mailto:${email}?subject=${encodeURIComponent("Richiesta info " + (property.name || ""))}`; },
      target: "_self",
      bg: "linear-gradient(135deg, #F97316, #DC2626)",
      icon: <Mail className="w-6 h-6 text-white" />,
    },
  ].filter(Boolean);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[200] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm"
        data-testid="contact-popup-overlay"
      >
        <motion.div
          initial={{ y: 60, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 60, opacity: 0, scale: 0.96 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full md:max-w-md bg-white rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden"
          data-testid="contact-popup"
        >
          {/* Drag handle (mobile only) */}
          <div className="md:hidden flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-zinc-300" />
          </div>

          <div className="px-6 pt-4 pb-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-bold text-zinc-900 tracking-tight">Contatta l'host</h3>
              <button onClick={onClose} className="w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors" data-testid="contact-popup-close">
                <X className="w-4 h-4 text-zinc-700" />
              </button>
            </div>
            <p className="text-xs text-zinc-500 mb-5">Scegli il canale preferito — risposta entro 1 ora</p>

            {channels.length === 0 ? (
              <div className="text-center py-6">
                <MessageCircle className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm text-zinc-400">L'host non ha ancora pubblicato i suoi contatti diretti.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {channels.map((c) => (
                  <motion.a
                    key={c.key}
                    href={c.href}
                    target={c.target}
                    rel={c.target === "_blank" ? "noopener noreferrer" : undefined}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={(e) => { if (c.action) { e.preventDefault(); c.action(); } onClose(); }}
                    className="flex items-center gap-4 p-3.5 rounded-2xl bg-zinc-50 hover:bg-zinc-100 transition-colors"
                    data-testid={`contact-${c.key}`}
                  >
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-md" style={{ background: c.bg }}>
                      {c.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-zinc-900 text-base">{c.label}</p>
                      <p className="text-xs text-zinc-500 truncate">{c.hint}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-400 shrink-0" />
                  </motion.a>
                ))}
              </div>
            )}

            <p className="text-[10px] text-zinc-400 text-center mt-5">
              Prenoti direttamente, niente commissioni di intermediazione.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const SharePopup = ({ open, onClose, property, theme }) => {
  const [copied, setCopied] = useState(false);
  if (!open || !property) return null;

  // Prefer custom slug URL if available
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const primaryUrl = property.custom_slug
    ? `${origin}/s/${property.custom_slug}`
    : `${origin}/property/${property.property_id}`;
  const title = property.name || "Hostland";
  // Preset invitation to install the property's WebApp when sharing the link
  // to a friend/family member. If the host has written a description we still
  // lead with it, then append the install invite so the guest sees both.
  const firstDescLine = property.description?.split("\n")[0]?.trim() || "";
  const pwaPitch = "Se installi la webapp riceverai notifiche su promozioni e sconti riservati (facoltativo, puoi ignorarla).";
  const text = firstDescLine
    ? `${firstDescLine}\n\n👉 ${pwaPitch}`
    : `🏡 ${title} — ecco tutte le info della struttura.\n\n👉 ${pwaPitch}`;

  const copyLink = async (url, label = "Link") => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(`${label} copiato`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const nativeShare = async () => {
    const data = { title, text, url: primaryUrl };
    try {
      if (navigator.share && navigator.canShare?.(data)) {
        await navigator.share(data);
        onClose();
      } else {
        copyLink(primaryUrl);
      }
    } catch (err) {
      if (err.name !== "AbortError") copyLink(primaryUrl);
    }
  };

  const encoded = encodeURIComponent(primaryUrl);
  const encodedText = encodeURIComponent(`${title} — ${text}`);

  const socials = [
    { name: "WhatsApp", href: `https://wa.me/?text=${encodedText}%20${encoded}`, bg: "linear-gradient(135deg, #25D366, #128C7E)", icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 2C6.477 2 2 6.477 2 12c0 1.997.594 3.85 1.6 5.4L2 22l4.6-1.6A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/></svg>
    )},
    { name: "Telegram", href: `https://t.me/share/url?url=${encoded}&text=${encodedText}`, bg: "linear-gradient(135deg, #229ED9, #1670A4)", icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8l-1.59 7.5c-.12.54-.43.67-.87.42l-2.4-1.77-1.16 1.12c-.13.13-.24.24-.49.24l.17-2.43 4.42-3.99c.19-.17-.04-.27-.3-.1l-5.46 3.44-2.35-.74c-.51-.16-.52-.51.11-.76l9.18-3.54c.42-.16.79.1.65.66z"/></svg>
    )},
    { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`, bg: "linear-gradient(135deg, #1877F2, #0c5cd2)", icon: <Facebook className="w-5 h-5 text-white" /> },
    { name: "X / Twitter", href: `https://twitter.com/intent/tweet?url=${encoded}&text=${encodedText}`, bg: "linear-gradient(135deg, #14171A, #2F3336)", icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
    )},
    { name: "Email", href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodedText}%20${encoded}`, bg: "linear-gradient(135deg, #6B7280, #374151)", icon: <Mail className="w-5 h-5 text-white" /> },
    { name: "SMS", href: `sms:?&body=${encodedText}%20${encoded}`, bg: "linear-gradient(135deg, #10B981, #047857)", icon: <MessageCircle className="w-5 h-5 text-white" /> },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[200] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm"
        data-testid="share-popup-overlay"
      >
        <motion.div
          initial={{ y: 60, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 60, opacity: 0, scale: 0.96 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full md:max-w-md bg-white rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden"
          data-testid="share-popup"
        >
          {/* Drag handle (mobile only) */}
          <div className="md:hidden flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-zinc-300" />
          </div>

          <div className="px-6 pt-3 pb-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-bold text-zinc-900 tracking-tight">Condividi</h3>
              <button onClick={onClose} className="w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors" data-testid="share-popup-close">
                <X className="w-4 h-4 text-zinc-700" />
              </button>
            </div>
            <p className="text-xs text-zinc-500 mb-5 truncate" title={title}>{title}</p>

            {/* Primary link with copy */}
            <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-3 mb-5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Link {property.custom_slug ? "personalizzato" : "diretto"}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs text-zinc-900 truncate font-mono">{primaryUrl}</code>
                <button
                  onClick={() => copyLink(primaryUrl)}
                  className="shrink-0 inline-flex items-center gap-1.5 bg-zinc-900 text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-zinc-800 transition-colors"
                  data-testid="share-copy-primary"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                  {copied ? "Copiato" : "Copia"}
                </button>
              </div>
              {property.custom_slug && (
                <button
                  onClick={() => copyLink(`${origin}/property/${property.property_id}`, "Link sistema")}
                  className="mt-2 text-[10px] text-zinc-500 hover:text-zinc-900 underline underline-offset-2"
                  data-testid="share-copy-secondary"
                >
                  Oppure copia il link tecnico
                </button>
              )}
            </div>

            {/* Native share (mobile-friendly) */}
            {typeof navigator !== "undefined" && navigator.share && (
              <button
                onClick={nativeShare}
                className="w-full bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-bold py-3 rounded-2xl mb-4 hover:shadow-lg transition-shadow flex items-center justify-center gap-2"
                data-testid="share-native"
              >
                <Share2 className="w-4 h-4" /> Condividi con il sistema
              </button>
            )}

            {/* Socials grid */}
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Oppure scegli</p>
            <div className="grid grid-cols-3 gap-3">
              {socials.map((s) => (
                <motion.a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -3, scale: 1.04 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-zinc-50 hover:bg-zinc-100 transition-colors"
                  data-testid={`share-social-${s.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-md" style={{ background: s.bg }}>
                    {s.icon}
                  </div>
                  <span className="text-[10px] font-bold text-zinc-700">{s.name}</span>
                </motion.a>
              ))}
            </div>

            {/* QR hint */}
            <p className="text-[10px] text-zinc-400 text-center mt-5">
              Tip: ogni struttura ha anche un QR code stampabile dalla dashboard.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const _OLD_StatsPillRow = ({ stats, theme, accent }) => (
  <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
    {stats.map((s, i) => (
      <motion.div
        key={s.label}
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.45, delay: i * 0.08 }}
        whileHover={{ y: -3, scale: 1.04 }}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl cursor-default"
        style={{ background: "#FAFAFB", border: `1px solid ${theme.border}` }}
        data-testid={`stat-pill-${s.label}`}
      >
        <s.icon className="w-4 h-4" style={{ color: accent }} />
        <span className="text-lg md:text-xl font-bold tracking-tight" style={{ color: theme.text }}>
          <AnimatedCounter value={s.val} duration={0.9} />
        </span>
        <span className="text-xs md:text-sm font-medium" style={{ color: theme.sub }}>{s.label}</span>
      </motion.div>
    ))}
  </div>
);

/* ─── CIN Badge (Codice Identificativo Nazionale, legally required on listings) ───
   Clickable: opens an educational modal that guides the guest to verify the CIN
   on the official Ministry of Tourism portal (copy-to-clipboard + external link).
   Pulses gently to invite the click; animation is disabled for users who set
   prefers-reduced-motion. */
const PROPERTY_TYPE_LABELS = {
  casa: { it: "Casa vacanze", en: "Holiday home", fr: "Maison de vacances", es: "Casa vacacional", de: "Ferienhaus" },
  appartamento: { it: "Appartamento", en: "Apartment", fr: "Appartement", es: "Apartamento", de: "Apartment" },
  bnb: { it: "B&B", en: "B&B", fr: "B&B", es: "B&B", de: "B&B" },
  agriturismo: { it: "Agriturismo", en: "Farm stay", fr: "Agritourisme", es: "Agroturismo", de: "Bauernhof" },
  locazione: { it: "Locazione turistica", en: "Short-term rental", fr: "Location touristique", es: "Alquiler turístico", de: "Ferienvermietung" },
  affittacamere: { it: "Affittacamere", en: "Guest rooms", fr: "Chambres d'hôtes", es: "Habitaciones", de: "Gästezimmer" },
  glamping: { it: "Glamping", en: "Glamping", fr: "Glamping", es: "Glamping", de: "Glamping" },
  villa: { it: "Villa", en: "Villa", fr: "Villa", es: "Villa", de: "Villa" },
  boutique: { it: "Boutique hotel", en: "Boutique hotel", fr: "Boutique hôtel", es: "Boutique hotel", de: "Boutique-Hotel" },
};

// Pill categoria struttura: mostra il tipo (B&B, agriturismo…) a colpo d'occhio.
const PropertyTypePill = ({ type, lang, accent }) => {
  const m = PROPERTY_TYPE_LABELS[type];
  if (!m) return null;
  const label = m[lang] || m.en || m.it;
  const c = accent || "#E11D2E";
  return (
    <span data-testid="property-type-pill"
      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-bold"
      style={{ background: c + "14", color: c, border: `1.5px solid ${c}33` }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3 11l9-7 9 7" /><path d="M5 10v10h14V10" />
      </svg>
      {label}
    </span>
  );
};

const LANG_ORDER = ["it", "en", "fr", "de", "es"];
const LANG_NAMES = { it: "Italiano", en: "English", fr: "Français", de: "Deutsch", es: "Español" };

// Selettore lingua compatto "IT ▾" senza bandiere (mobile + desktop).
const LangDropdown = ({ round = false }) => {
  const { lang, changeLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}
        aria-label="Seleziona lingua"
        className={round
          ? "inline-flex items-center justify-center gap-0.5 w-11 h-11 rounded-full text-[13px] font-semibold"
          : "inline-flex items-center gap-1 h-11 px-4 rounded-full text-sm font-semibold"}
        style={{ border: "1px solid #ECECEE", color: "#1C1C1E", background: "#fff" }}
        data-testid="lang-dropdown-btn">
        <span>{(lang || "it").toUpperCase()}</span>
        <span className="text-[15px] leading-none">{LANG_FLAGS[lang || "it"]}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-2 w-44 rounded-2xl bg-white py-1.5 z-[90]"
          style={{ border: "1px solid #ECECEE", boxShadow: "0 16px 40px -12px rgba(0,0,0,0.2)" }} data-testid="lang-dropdown-menu">
          {LANG_ORDER.map((l) => (
            <button key={l} type="button" role="menuitem" onClick={() => { changeLang(l); setOpen(false); }}
              className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium hover:bg-zinc-50 transition-colors"
              style={{ color: lang === l ? "#A8261A" : "#1C1C1E" }} data-testid={`lang-${l}`}>
              <span className="flex items-center gap-2"><span className="text-[15px] leading-none">{LANG_FLAGS[l]}</span>{LANG_NAMES[l]}</span><span className="text-xs font-bold opacity-70">{l.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const LANG_FLAGS = { it: "🇮🇹", en: "🇬🇧", fr: "🇫🇷", de: "🇩🇪", es: "🇪🇸" };

// Selettore lingua glass con bandiera, per la barra sticky rossa in basso (mobile).
const LangDropdownGlass = () => {
  const { lang, changeLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative shrink-0" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}
        aria-label="Seleziona lingua"
        className="w-11 h-11 rounded-2xl flex items-center justify-center text-[20px]"
        style={{ background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.28)", WebkitTapHighlightColor: "transparent" }}
        data-testid="mobile-lang-btn">
        <span style={{ lineHeight: 1 }}>{LANG_FLAGS[lang || "it"]}</span>
      </button>
      {open && (
        <div role="menu" className="absolute left-0 bottom-full mb-2 w-44 rounded-2xl bg-white py-1.5 z-[90]"
          style={{ border: "1px solid #ECECEE", boxShadow: "0 16px 40px -12px rgba(0,0,0,0.25)" }} data-testid="mobile-lang-menu">
          {LANG_ORDER.map((l) => (
            <button key={l} type="button" role="menuitem" onClick={() => { changeLang(l); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium hover:bg-zinc-50 transition-colors"
              style={{ color: lang === l ? "#A8261A" : "#1C1C1E" }} data-testid={`mobile-lang-${l}`}>
              <span className="text-[18px] leading-none">{LANG_FLAGS[l]}</span><span>{LANG_NAMES[l]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Icone social monocromatiche in cerchi 44px con bordo sottile.
const SocialRowMono = ({ property, suffix = "" }) => {
  const C = "inline-flex items-center justify-center w-11 h-11 rounded-full transition-colors hover:bg-zinc-50";
  const S = { border: "1px solid #ECECEE", color: "#1C1C1E" };
  const items = [];
  if (property.social_instagram) items.push(["instagram", property.social_instagram, <Instagram className="w-[18px] h-[18px]" />, "Instagram"]);
  if (property.social_facebook) items.push(["facebook", property.social_facebook, <Facebook className="w-[18px] h-[18px]" />, "Facebook"]);
  if (property.social_tiktok) items.push(["tiktok", property.social_tiktok, (
    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1Z" /></svg>
  ), "TikTok"]);
  if (!items.length) return null;
  return (
    <div className="flex items-center gap-2">
      {items.map(([k, href, icon, label]) => (
        <a key={k} href={href} target="_blank" rel="noopener noreferrer" className={C} style={S} aria-label={label} data-testid={`social-${k}${suffix}`}>
          {icon}
        </a>
      ))}
    </div>
  );
};



const CinBadge = ({ cin, size = "md" }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inlineCopied, setInlineCopied] = useState(false);
  const [clipboardFailed, setClipboardFailed] = useState(false);
  const codeInputRef = useRef(null);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!cin) return null;
  const padY = size === "sm" ? "py-1" : "py-1.5";
  const padX = size === "sm" ? "px-2.5" : "px-3";
  const fontSize = size === "sm" ? "text-[10px]" : "text-[11px]";
  const iconSize = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";

  const PORTAL_URL = "https://bdsr.ministeroturismo.gov.it/ricerca-cin";

  const copyCin = async () => {
    try {
      await navigator.clipboard.writeText(cin);
      return true;
    } catch {
      return false;
    }
  };

  const handleCopyAndOpen = async () => {
    const ok = await copyCin();
    if (ok) {
      setCopied(true);
      toast.success("CIN copiato negli appunti");
      setTimeout(() => setCopied(false), 2000);
      window.open(PORTAL_URL, "_blank", "noopener,noreferrer");
    } else {
      setClipboardFailed(true);
      // Focus the fallback input so the user can select+copy manually.
      setTimeout(() => codeInputRef.current?.select?.(), 100);
    }
  };

  const handleInlineCopy = async () => {
    const ok = await copyCin();
    if (ok) {
      setInlineCopied(true);
      setTimeout(() => setInlineCopied(false), 1500);
    } else {
      setClipboardFailed(true);
      setTimeout(() => codeInputRef.current?.select?.(), 100);
    }
  };

  return (
    <>
      <style>{`@keyframes cinPulse{0%,100%{box-shadow:0 0 0 0 rgba(168,38,26,0.28)}50%{box-shadow:0 0 0 7px rgba(168,38,26,0)}}.cin-pulse{animation:cinPulse 2.6s ease-in-out infinite;cursor:pointer}.cin-pulse:hover{background:#FDE3DF !important}@media (prefers-reduced-motion:reduce){.cin-pulse{animation:none}}`}</style>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="cin-pulse inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300"
        style={{ background: "#FEF1EF", color: "#A8261A", fontSize: "12px", fontWeight: 600, letterSpacing: "0.01em" }}
        data-testid="cin-badge"
        aria-label="Codice Identificativo Nazionale, mostra dettagli"
      >
        <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.4} />
        <span style={{ fontVariantNumeric: "tabular-nums" }}>CIN {cin}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[220] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4"
            data-testid="cin-verify-overlay"
          >
            <motion.div
              initial={{ y: 60, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 60, opacity: 0, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full md:max-w-lg bg-white rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
              data-testid="cin-verify-modal"
            >
              <div className="md:hidden flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-zinc-300" />
              </div>

              <div className="px-6 md:px-7 pt-5 pb-6">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div className="min-w-0 flex-1 text-left">
                    <div className="inline-flex items-center gap-2 rounded-full bg-red-50 px-2.5 py-1 mb-2.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-red-600" strokeWidth={2.6} />
                      <span className="text-[10px] font-black uppercase tracking-[0.14em] text-red-700">Verifica ufficiale</span>
                    </div>
                    <h3 className="text-[22px] md:text-[24px] font-black text-zinc-900 tracking-tight leading-tight mb-1.5">
                      Verifica tu stesso se il CIN esiste
                    </h3>
                    <p className="text-[13.5px] text-zinc-500 leading-snug">
                      In 30 secondi verifichi che questo CIN sia reale e corrisponda alla struttura, sul sito ufficiale del Ministero del Turismo.
                    </p>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="min-w-[40px] min-h-[40px] w-10 h-10 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors shrink-0"
                    data-testid="cin-modal-close"
                    aria-label="Chiudi"
                  >
                    <X className="w-4 h-4 text-zinc-700" />
                  </button>
                </div>

                {/* Steps — left-aligned, tight vertical rhythm */}
                <ol className="mb-6 divide-y divide-zinc-100 border-y border-zinc-100">
                  {[
                    <>Clicca su <span className="font-bold text-zinc-900">"Copia CIN e vai al portale"</span></>,
                    <>Si aprirà il sito del Ministero del Turismo in una nuova scheda</>,
                    <>Incolla il codice nel campo di ricerca <span className="text-zinc-500">(Ctrl+V su PC · tocco lungo + "Incolla" su mobile)</span></>,
                    <>Spunta <span className="font-semibold text-zinc-900">"Non sono un robot"</span> se richiesto</>,
                    <>Clicca su <span className="font-bold text-zinc-900">"Cerca"</span></>,
                    <>Controlla che <span className="font-semibold text-zinc-900">città e nome struttura</span> corrispondano a questa proprietà</>,
                  ].map((content, idx) => (
                    <li key={idx} className="flex items-start gap-3 py-3">
                      <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-red-600 text-white font-black text-[11px] flex items-center justify-center tabular-nums">
                        {idx + 1}
                      </span>
                      <span className="text-[14.5px] leading-[1.5] text-zinc-700 text-left">
                        {content}
                      </span>
                    </li>
                  ))}
                </ol>

                {/* Primary CTA */}
                <button
                  type="button"
                  onClick={handleCopyAndOpen}
                  className="w-full min-h-[52px] rounded-2xl bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold text-[15px] px-5 py-3.5 shadow-[0_10px_30px_-10px_rgba(220,38,38,0.5)] transition-all flex items-center justify-center gap-2"
                  data-testid="cin-copy-and-open-btn"
                >
                  {copied ? (
                    <><Check className="w-5 h-5" /> CIN copiato — apri portale</>
                  ) : (
                    <><Copy className="w-5 h-5" /> Copia CIN e vai al portale</>
                  )}
                </button>

                {/* Code + inline copy — LEFT aligned */}
                <div className="mt-4 flex items-center gap-2 text-[13px] text-zinc-500">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Codice</span>
                  <span className="font-mono font-black text-zinc-900 tracking-wide" data-testid="cin-modal-code">{cin}</span>
                  <button
                    type="button"
                    onClick={handleInlineCopy}
                    className="ml-auto min-w-[36px] min-h-[36px] w-9 h-9 rounded-lg hover:bg-zinc-100 flex items-center justify-center transition-colors"
                    data-testid="cin-inline-copy-btn"
                    aria-label="Copia il codice CIN"
                  >
                    {inlineCopied
                      ? <Check className="w-4 h-4 text-emerald-600" />
                      : <Copy className="w-4 h-4 text-zinc-500" />}
                  </button>
                </div>

                {clipboardFailed && (
                  <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-left">
                    <p className="text-xs font-semibold text-amber-800 mb-2">
                      Seleziona e copia manualmente il codice qui sotto:
                    </p>
                    <input
                      ref={codeInputRef}
                      readOnly
                      value={cin}
                      onFocus={(e) => e.target.select()}
                      className="w-full font-mono font-bold text-sm text-zinc-900 bg-white border border-amber-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      data-testid="cin-fallback-input"
                    />
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};


const PropertyLanding = () => {
  // /property/:propertyId or /s/:slug — the landing API resolves both; after the
  // first fetch every other call uses the real property_id.
  const { propertyId: paramId, slug } = useParams();
  const [resolvedId, setResolvedId] = useState(paramId || null);
  const propertyId = paramId || resolvedId;
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t, lang, changeLang, FLAGS, LANGS, setTranslating } = useLanguage();
  const carouselRef = useRef(null);

  const [property, setProperty] = useState(null);
  const [host, setHost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showHostProfile, setShowHostProfile] = useState(false);
  const [weather, setWeather] = useState(null);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [liked, setLiked] = useState(false);
  const [showRegBanner, setShowRegBanner] = useState(false);
  const [otherProperties, setOtherProperties] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [translatedContent, setTranslatedContent] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  // Segnalazione contenuto (DSA notice-and-action)
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("illecito");
  const [reportNote, setReportNote] = useState("");
  const [reportSending, setReportSending] = useState(false);
  const submitReport = async () => {
    setReportSending(true);
    try {
      await axios.post(`${API}/properties/${propertyId}/report`, { reason: reportReason, note: reportNote });
      toast.success("Segnalazione inviata. Grazie, la esamineremo al più presto.");
      setShowReport(false); setReportNote(""); setReportReason("illecito");
    } catch (e) {
      toast.error("Invio non riuscito, riprova.");
    } finally { setReportSending(false); }
  };
  // Multi-property dropdown ("Le nostre strutture") — only meaningful when the host owns >1 property
  const [showOtherPropsMenu, setShowOtherPropsMenu] = useState(false);
  const otherPropsMenuRef = useRef(null);
  useEffect(() => {
    if (!showOtherPropsMenu) return;
    const onDocClick = (e) => {
      if (otherPropsMenuRef.current && !otherPropsMenuRef.current.contains(e.target)) {
        setShowOtherPropsMenu(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showOtherPropsMenu]);

  // Escape to close lightbox + lock body scroll
  useEffect(() => {
    if (!showAllPhotos) return;
    const onKey = (e) => { if (e.key === "Escape") setShowAllPhotos(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [showAllPhotos]);

  useEffect(() => { fetchPropertyData(); }, [paramId, slug]);

  // ─── DYNAMIC SEO META TAGS + JSON-LD LodgingBusiness per indicizzazione ───
  useEffect(() => {
    if (!property) return;
    try {
    const PROD_DOMAIN = "https://hostland.vitadahost.it";
    // ⚠️ property.comune can be a plain string (legacy data) OR a Dict
    // ({nome, istat, sigla, provincia, regione, cap, popolazione}) from the
    // autocomplete. Always extract the city name string, never pass the raw
    // object — rendering it as React child triggers React error #31.
    const comuneName = typeof property.comune === "string"
      ? property.comune
      : (property.comune && property.comune.nome) || "";
    const city = comuneName || property.address?.split(",")?.pop()?.trim() || "Italia";
    const name = property.name || "Hostland";
    const canonical = property.custom_slug
      ? `${PROD_DOMAIN}/s/${property.custom_slug}`
      : `${PROD_DOMAIN}/property/${property.property_id}`;
    const baseDesc = (property.description || "").split("\n")[0].slice(0, 140);
    const title = `${name} — ${city} | Casa vacanza senza commissioni · Hostland`;
    // AI-generated meta description takes priority (Claude Sonnet 5 output, 150-160 chars,
    // localized per language). Falls back to the legacy formula when not yet generated.
    const aiMeta = property.meta_descriptions?.[lang];
    const description = aiMeta
      ? aiMeta
      : baseDesc
        ? `${baseDesc}. Prenota direttamente l'host a ${city}, zero commissioni di intermediazione su Hostland.`
        : `${name} a ${city}: prenota direttamente con l'host, ZERO commissioni, sconti riservati, GuestBook digitale incluso.`;
    const firstMedia = property.media?.[0];
    const firstMediaStr = typeof firstMedia === "string" ? firstMedia : (firstMedia?.url || "");
    // Prefer the dynamic, server-rendered OG image (1200×630 with cover photo,
    // property name, city and CIN ribbon) — it produces beautiful share previews
    // on WhatsApp / Telegram / FB / LinkedIn. Falls back to the first photo or
    // the brand social image when the property has no media yet.
    const ogImage = property.is_published
      ? `${PROD_DOMAIN}/api/og/property/${property.property_id}.png`
      : firstMediaStr
        ? (firstMediaStr.startsWith("http") ? firstMediaStr : `${PROD_DOMAIN}/api/files/${firstMediaStr}?w=1200`)
        : `${PROD_DOMAIN}/hostland-social.png`;

    const setMeta = (key, val, attr = "name") => {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", val);
    };

    document.title = title;
    setMeta("description", description);
    setMeta("keywords", `${name.toLowerCase()}, ${city.toLowerCase()}, casa vacanze ${city.toLowerCase()}, affitto breve ${city.toLowerCase()}, prenotazione diretta, zero commissioni, hostland, ${property.cin ? `CIN ${property.cin}` : ""}`.trim());
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:type", "place", "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:locale", "it_IT", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);

    // canonical
    let canonTag = document.querySelector('link[rel="canonical"]');
    if (!canonTag) {
      canonTag = document.createElement("link");
      canonTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonTag);
    }
    canonTag.setAttribute("href", canonical);

    // ─── PWA manifest — per-property so Chrome/Android/Safari install THIS
    //      property as its own icon on the home screen (unique `manifest.id`).
    //
    // ⚠️ iOS Safari (and macOS Safari) pick the FIRST `<link rel="manifest">`
    // and the SIZED `apple-touch-icon` closest to the requested size. So if
    // we only APPEND our property tags, they lose to the default Hostland
    // ones from index.html — the bug the host hit ("Aggiungi a Home mostra
    // l'icona di Hostland invece della struttura"). We must therefore:
    //   1. Detach every non-property manifest & apple-touch-icon
    //   2. Re-inject the property manifest with cache-buster so Safari
    //      parses fresh (Safari caches manifest by URL forever otherwise)
    //   3. Inject apple-touch-icons for EVERY size iOS looks for
    //      (152, 167, 180) — otherwise iOS matches the sized default first.
    // On unmount we reattach the originals so the public landing works normal.
    const detached = [];
    document.querySelectorAll('link[rel="manifest"]:not([data-property]), link[rel="apple-touch-icon"]:not([data-property]), link[rel="apple-touch-icon-precomposed"]:not([data-property])').forEach((el) => {
      detached.push(el);
      el.parentNode.removeChild(el);
    });

    let manifestTag = document.querySelector('link[rel="manifest"][data-property]');
    if (!manifestTag) {
      manifestTag = document.createElement("link");
      manifestTag.setAttribute("rel", "manifest");
      manifestTag.setAttribute("data-property", "1");
      document.head.appendChild(manifestTag);
    }

    // Compute absolute icon URL once. Prefer 512 (Apple upscales as needed),
    // fall back to 192 and finally the Hostland logo if nothing custom.
    const rawIcon = property.pwa_config?.icon_512 || property.pwa_config?.icon_192 || "/hostland-logo.png";
    const iconAbs = rawIcon.startsWith("http") ? rawIcon : `${BACKEND_URL}${rawIcon}`;

    // ⚠️ Safari (iOS + macOS) caches the fetched manifest AGGRESSIVELY and
    // often reads it before React has swapped the default `href`. Result:
    // "Aggiungi al Dock" showed "Hostland" + hostland.vitadahost.it. Fix:
    // build the manifest inline as a `data:application/manifest+json;utf-8`
    // URL. Safari parses it synchronously from the DOM, no network fetch
    // → no cache, no stale content, no race with our React swap.
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    // Il manifest punta all'ENDPOINT dinamico del backend (stesso che usa
    // l'iniettore sincrono di index.html), NON a un data: URL: Safari iOS
    // gestisce male i data: manifest per "Aggiungi a Home". Lo slug (custom o
    // property_id) viene risolto dal backend. Cache-buster su updated_at così
    // un cambio di icona/nome si riflette subito.
    const pwaSlug = property.custom_slug || property.property_id;
    const manifestHref = `${origin}/api/pwa/manifest/property/${encodeURIComponent(pwaSlug)}`
      + (property.updated_at ? `?v=${encodeURIComponent(property.updated_at)}` : "");
    manifestTag.setAttribute("href", manifestHref);

    setMeta("theme-color", (property.pwa_config?.theme_color) || "#FF385C");
    setMeta("apple-mobile-web-app-capable", "yes");
    setMeta("mobile-web-app-capable", "yes");
    setMeta("apple-mobile-web-app-status-bar-style", "black-translucent");
    setMeta("apple-mobile-web-app-title", (property.pwa_config?.short_name) || (property.pwa_config?.name) || name);

    // Purge any older per-property apple-touch-icons before reinjecting
    // (property changed → old icon URL must go).
    document.querySelectorAll('link[rel="apple-touch-icon"][data-property]').forEach((el) => {
      el.parentNode.removeChild(el);
    });
    ["152x152", "167x167", "180x180", "192x192"].forEach((size) => {
      const li = document.createElement("link");
      li.setAttribute("rel", "apple-touch-icon");
      li.setAttribute("sizes", size);
      li.setAttribute("href", iconAbs);
      li.setAttribute("data-property", "1");
      document.head.appendChild(li);
    });
    // Unsized fallback (Safari falls back to this if no sized match)
    const unsized = document.createElement("link");
    unsized.setAttribute("rel", "apple-touch-icon");
    unsized.setAttribute("href", iconAbs);
    unsized.setAttribute("data-property", "1");
    document.head.appendChild(unsized);

    // Reattach the default manifest + apple-touch-icons when the user leaves
    // the property landing (SPA navigation). This runs in the effect cleanup
    // below — bookkeeping stashed on the DOM node so we can find it later.
    manifestTag.__hostland_detached = detached;

    // ─── OneSignal init (only on public Property Landing, NOT Welcome Book) ─
    // We defer to `initOneSignalForProperty` which lazy-loads the SDK, calls
    // OneSignal.init() once, then tags this subscription with the current
    // property_id + host_id so the host can address these guests later
    // from their dashboard.
    //
    // Skip on:
    //  - preview iframe inside the admin editor (`?preview=1`) — OneSignal
    //    would try to init on a non-whitelisted domain, throw, and add noise
    //    to the console. Also we NEVER want to prompt an admin previewing
    //    their own page.
    //  - other embedded iframes (`?embed=1`, `?nocookie=1`).
    let inPreviewFrame = false;
    try {
      const q = new URLSearchParams(window.location.search);
      inPreviewFrame = q.get("preview") === "1" || q.get("embed") === "1" || q.get("nocookie") === "1" || window.self !== window.top;
    } catch { inPreviewFrame = true; /* cross-origin frame */ }
    if (property.property_id && !inPreviewFrame) {
      initOneSignalForProperty(
        property.property_id,
        property.host_id || "",
        (typeof lang === "string" ? lang : "it"),
      );

      // ─── Metrica INSTALLAZIONI (distinta dagli iscritti push) ───
      // Registriamo un'installazione quando l'app gira come installata
      // (standalone / lanciata dall'icona con ?source=pwa) o quando scatta
      // l'evento appinstalled. device_id stabile in localStorage → conteggio
      // per dispositivo. Non richiede il permesso notifiche.
      try {
        const regInstall = () => {
          let did = localStorage.getItem("hostland_pwa_device");
          if (!did) {
            did = "dev_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
            try { localStorage.setItem("hostland_pwa_device", did); } catch {}
          }
          fetch(`${BACKEND_URL}/api/property/${property.property_id}/install/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ device_id: did, platform: (navigator.userAgent || "").slice(0, 60) }),
            keepalive: true,
          }).catch(() => {});
        };
        const isStandalone = (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
          || window.navigator.standalone === true
          || new URLSearchParams(window.location.search).get("source") === "pwa";
        if (isStandalone) regInstall();
        window.addEventListener("appinstalled", regInstall, { once: true });
      } catch { /* no-op */ }
    }

    // ─── hreflang alternates ───
    // Puntiamo TUTTE le varianti linguistiche alla stessa PropertyLanding
    // (canonical) con `?lang=xx`. Il GuestBook (/guestbook/{id}) NON viene
    // mai referenziato come alternate: è una risorsa privata riservata agli
    // ospiti e non deve essere scoperta né indicizzata dai motori di ricerca.
    const HREFLANG_TARGETS = [
      { lang: "it", href: canonical },
      { lang: "en", href: `${canonical}?lang=en` },
      { lang: "fr", href: `${canonical}?lang=fr` },
      { lang: "es", href: `${canonical}?lang=es` },
      { lang: "de", href: `${canonical}?lang=de` },
      { lang: "x-default", href: canonical },
    ];
    // Wipe previous hreflangs we control (data-hostland), keep any third-party tags
    document.querySelectorAll('link[rel="alternate"][data-hostland]').forEach((el) => el.remove());
    HREFLANG_TARGETS.forEach(({ lang, href }) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", lang);
      link.setAttribute("href", href);
      link.setAttribute("data-hostland", "1");
      document.head.appendChild(link);
    });

    // Build amenityFeature array from the property's amenities list — schema.org
    // expects LocationFeatureSpecification entries with name + value=true.
    const amenityFeature = (property.amenities || []).slice(0, 30).map((a) => ({
      "@type": "LocationFeatureSpecification",
      name: String(a),
      value: true,
    }));

    // Optional review list — pulled from google_reviews when imported.
    const reviewItems = (property.google_reviews || []).slice(0, 5).map((rv) => ({
      "@type": "Review",
      author: { "@type": "Person", name: rv.author_name || "Anonimo" },
      reviewRating: { "@type": "Rating", ratingValue: rv.rating || 5, bestRating: 5 },
      reviewBody: (rv.text || "").slice(0, 280),
      ...(rv.time ? { datePublished: new Date(rv.time * 1000).toISOString().slice(0, 10) } : {}),
    }));

    // JSON-LD LodgingBusiness for the property
    const ld = {
      "@context": "https://schema.org",
      "@type": "LodgingBusiness",
      "@id": canonical,
      name,
      description: baseDesc || description,
      url: canonical,
      inLanguage: ["it", "en", "fr", "es", "de"],
      image: (property.media || []).slice(0, 5).map((m) => {
        const s = typeof m === "string" ? m : (m?.url || "");
        if (!s) return null;
        return s.startsWith("http") ? s : `${PROD_DOMAIN}/api/files/${s}?w=1200`;
      }).filter(Boolean),
      ...(property.address || city
        ? {
            address: {
              "@type": "PostalAddress",
              streetAddress: property.address || "",
              addressLocality: city,
              addressCountry: "IT",
            },
          }
        : {}),
      ...(property.location_lat && property.location_lng
        ? {
            geo: {
              "@type": "GeoCoordinates",
              latitude: property.location_lat,
              longitude: property.location_lng,
            },
          }
        : {}),
      ...(property.cin || property.legal_cir
        ? {
            identifier: [
              ...(property.cin ? [{ "@type": "PropertyValue", propertyID: "CIN", value: property.cin }] : []),
              ...(property.legal_cir ? [{ "@type": "PropertyValue", propertyID: "CIR", value: property.legal_cir }] : []),
            ],
          }
        : {}),
      ...(property.phone ? { telephone: property.phone } : {}),
      ...(property.email ? { email: property.email } : {}),
      ...(property.checkin_time ? { checkinTime: property.checkin_time } : {}),
      ...(property.checkout_time ? { checkoutTime: property.checkout_time } : {}),
      ...(property.legal_business_name ? { legalName: property.legal_business_name } : {}),
      priceRange: "€€",
      paymentAccepted: "Cash, Credit Card, Bank Transfer",
      currenciesAccepted: "EUR",
      ...(property.guests_count ? { occupancy: { "@type": "QuantitativeValue", maxValue: property.guests_count } } : {}),
      ...(property.rooms_count ? { numberOfRooms: property.rooms_count } : {}),
      ...(property.bathrooms_count ? { numberOfBathroomsTotal: property.bathrooms_count } : {}),
      ...(amenityFeature.length ? { amenityFeature } : {}),
      ...(reviewItems.length ? { review: reviewItems } : {}),
      ...(property.google_rating
        ? {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: property.google_rating,
              reviewCount: property.google_review_count || 1,
              bestRating: 5,
              worstRating: 1,
            },
          }
        : {}),
      potentialAction: {
        "@type": "ReserveAction",
        target: canonical,
        description: "Prenota direttamente, zero commissioni",
      },
    };
    let ldTag = document.getElementById("property-jsonld");
    if (!ldTag) {
      ldTag = document.createElement("script");
      ldTag.setAttribute("type", "application/ld+json");
      ldTag.id = "property-jsonld";
      document.head.appendChild(ldTag);
    }
    ldTag.textContent = JSON.stringify(ld);

    // ─── Breadcrumb JSON-LD ───
    // Google uses BreadcrumbList to render the path Hostland › Case vacanze · Roma › Villa Camelia
    // directly under the result title. Higher CTR.
    const slugCity = String(city || "italia")
      .toLowerCase()
      .replace(/['']/g, "")
      .replace(/\s+/g, "-");
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Hostland", item: PROD_DOMAIN },
        { "@type": "ListItem", position: 2, name: `Case vacanze ${city}`, item: `${PROD_DOMAIN}/case-vacanze/${slugCity}` },
        { "@type": "ListItem", position: 3, name, item: canonical },
      ],
    };
    let crumbTag = document.getElementById("property-breadcrumb-jsonld");
    if (!crumbTag) {
      crumbTag = document.createElement("script");
      crumbTag.setAttribute("type", "application/ld+json");
      crumbTag.id = "property-breadcrumb-jsonld";
      document.head.appendChild(crumbTag);
    }
    crumbTag.textContent = JSON.stringify(breadcrumb);

    return () => {
      ["property-jsonld", "property-breadcrumb-jsonld"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
      document.querySelectorAll('link[rel="alternate"][data-hostland]').forEach((el) => el.remove());
      // ─── Reattach default PWA head tags when leaving the property landing ─
      // If we don't, navigating from `/property/X` back to `/` leaves the
      // Hostland site with the property's manifest/apple-touch-icon and the
      // NEXT install would still be the wrong branding.
      const propManifest = document.querySelector('link[rel="manifest"][data-property]');
      const stashed = propManifest?.__hostland_detached || [];
      document.querySelectorAll('link[rel="manifest"][data-property], link[rel="apple-touch-icon"][data-property], link[rel="apple-touch-icon-precomposed"][data-property]').forEach((el) => el.remove());
      stashed.forEach((el) => document.head.appendChild(el));
    };
    } catch (err) {
      console.warn("SEO meta injection failed (non-fatal):", err);
    }
  }, [property, lang]);

  useEffect(() => {
    if (!propertyId) return;
    if (user && token) {
      axios.get(`${API}/favorites/ids`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setLiked(r.data.includes(propertyId))).catch(() => {});
    } else {
      const localFavs = JSON.parse(localStorage.getItem("hostland_favs") || "[]");
      setLiked(localFavs.includes(propertyId));
    }
  }, [propertyId, user, token]);

  useEffect(() => {
    if (!propertyId) return;
    const deviceHash = getDeviceHash();
    sessionStorage.setItem(`scan_${propertyId}_${deviceHash}`, "1");
  }, [propertyId]);

  const fetchReviews = useCallback(() => {
    if (!propertyId) return;
    axios.get(`${API}/properties/${propertyId}/reviews`).then(r => setReviews(r.data)).catch(() => {});
  }, [propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    axios.get(`${API}/properties/${propertyId}/host-others`).then(r => setOtherProperties(r.data)).catch(() => {});
    fetchReviews();
  }, [propertyId, fetchReviews]);

  // Blog: fetch localized posts (backend restituisce i post già tradotti per
  // la lingua corrente, con cache per-post). Refetch al cambio lingua.
  // Refresh automatico ogni 48h (default) + quando la scheda torna in primo
  // piano, così i nuovi articoli compaiono sempre senza refresh manuale.
  useEffect(() => {
    if (!propertyId) return;
    const BLOG_REFRESH_MS = 48 * 60 * 60 * 1000; // 48 ore
    let cancelled = false;
    const fetchBlog = () => {
      axios
        .get(`${API}/properties/${propertyId}/blog?limit=6&lang=${lang}&_ts=${Date.now()}`)
        .then(r => { if (!cancelled) setBlogPosts(r.data); })
        .catch(() => {});
    };
    fetchBlog();
    const interval = setInterval(fetchBlog, BLOG_REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === "visible") fetchBlog(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [propertyId, lang]);

  // Auto-translate user content when language is not Italian.
  // Fires IMMEDIATELY on mount (parallel with fetchPropertyData) so the
  // description is already translated when the guest sees the page — no
  // more Italian → French flicker. The backend translations are cached
  // per-language after the first call.
  useEffect(() => {
    if (!propertyId || lang === "it") {
      setTranslatedContent(null);
      setTranslating(false);
      return;
    }
    let cancelled = false;
    setTranslating(true);
    axios.get(`${API}/properties/${propertyId}/translations/${lang}`)
      .then(r => { if (!cancelled) setTranslatedContent(r.data); })
      .catch(() => { if (!cancelled) setTranslatedContent(null); })
      .finally(() => { if (!cancelled) setTranslating(false); });
    return () => { cancelled = true; };
  }, [propertyId, lang]);

  const fetchPropertyData = async () => {
    try {
      const deviceHash = getDeviceHash();
      const response = await axios.get(`${API}/properties/${paramId || slug}/landing?device_id=${deviceHash}`);
      setResolvedId(response.data.property.property_id);
      // Conta la visualizzazione (click) qualunque sia la provenienza: ricerca
      // "zero commissioni", QR, welcome book o link diretto. Il backend deduplica
      // per fingerprint su finestra 24h, quindi niente doppi conteggi.
      try { fetch(`${API}/properties/${response.data.property.property_id}/track-click`, { method: "POST", keepalive: true }); } catch (e) { /* non bloccante */ }
      setProperty(response.data.property);
      setHost(response.data.host);
      if (response.data.property.location_lat && response.data.property.location_lng) {
        fetchWeather(response.data.property.location_lat, response.data.property.location_lng);
      }
    } catch (error) { console.error("Error fetching property:", error); }
    finally { setLoading(false); }
  };

  const fetchWeather = async (lat, lng) => {
    try {
      // Open-Meteo with DWD ICON model (Deutsche Wetterdienst — German national weather
      // service). For continental Europe/Italy this model is significantly more accurate
      // than the default `best_match` blend; it's used by national meteorological services.
      // Falls back to the default forecast if ICON has gaps in the requested range.
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
        `&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m,apparent_temperature` +
        `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max` +
        `&forecast_days=7&timezone=auto&models=icon_seamless`;
      const r = await fetch(url);
      const data = await r.json();
      setWeather({ ...data.current, daily: data.daily });
    } catch {
      // Soft-fail: weather widgets gracefully skip when data is missing.
    }
  };

  // Fetch public availability for occupancy calc (next 90 days)
  const [occupancyPct, setOccupancyPct] = useState(null);
  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      try {
        const r = await fetch(`${API}/properties/${propertyId}/availability-public`);
        const data = await r.json();
        const blocks = data.blocks || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const horizonDays = 90;
        const horizonEnd = new Date(today.getTime() + horizonDays * 86400000);
        // Dedupe by date (defense in depth: backend already dedupes but old caches
        // may still return duplicate rows when the same day is blocked from
        // multiple OTA sources). Counting the same date twice would push the
        // percentage past the number of days in the horizon → cap at 100 anyway.
        const uniqueBlocked = new Set();
        for (const b of blocks) {
          if (!b || !b.blocked || !b.date) continue;
          const d = new Date(b.date);
          if (Number.isNaN(d.getTime())) continue;
          if (d >= today && d < horizonEnd) uniqueBlocked.add(b.date);
        }
        const pct = Math.min(100, Math.round((uniqueBlocked.size / horizonDays) * 100));
        setOccupancyPct(pct);
      } catch {
        setOccupancyPct(0);
      }
    })();
  }, [propertyId]);

  const getYouTubeId = (url) => {
    if (!url) return null;
    // Supporta: watch?v=, youtu.be/, embed/, v/, shorts/, live/, con eventuali querystring
    // Regex tollerante — accetta anche URL con parametri aggiuntivi (?feature=share, ?si=..., ecc.)
    const m = String(url).match(/(?:youtube\.com\/(?:watch\?(?:.*?&)?v=|embed\/|v\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
  };

  const getYouTubeThumbnail = (url) => {
    const id = getYouTubeId(url);
    // hqdefault.jpg è sempre disponibile per QUALSIASI video pubblico (anche vecchi).
    // maxresdefault.jpg è più bello ma manca su molti video → useremo onError() per il fallback.
    return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
  };

  const handleToggleLike = async () => {
    if (user && token) {
      try {
        const res = await axios.post(`${API}/favorites/${propertyId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setLiked(res.data.liked);
        toast.success(res.data.liked ? "Salvato nei preferiti" : "Rimosso dai preferiti");
      } catch { toast.error("Errore"); }
    } else {
      const localFavs = JSON.parse(localStorage.getItem("hostland_favs") || "[]");
      if (localFavs.includes(propertyId)) {
        localStorage.setItem("hostland_favs", JSON.stringify(localFavs.filter(id => id !== propertyId)));
        setLiked(false);
      } else {
        localStorage.setItem("hostland_favs", JSON.stringify([...localFavs, propertyId]));
        setLiked(true);
        if (!user) setTimeout(() => setShowRegBanner(true), 600);
      }
    }
  };

  const [shareOpen, setShareOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const handleShare = () => setShareOpen(true);

  // Preview override: l'editor passa ?pt=<preset>&pf=<font> per mostrare lo stile
  // scelto senza doverlo salvare. In produzione i params sono assenti.
  const previewOverride = useMemo(() => {
    if (typeof window === "undefined") return {};
    const q = new URLSearchParams(window.location.search);
    return { pt: q.get("pt"), pf: q.get("pf") };
  }, []);
  const preset = useMemo(() => getPreset(previewOverride.pt || property?.theme_preset), [property?.theme_preset, previewOverride.pt]);
  const theme = useMemo(() => ({ ...FORCED_THEME, accent: preset.accent }), [preset]);
  const fontFamily = previewOverride.pf || property?.theme_font || preset.font || "Inter";
  // Tinta "estiva" dei box canvas: per Hostland (default) resta null → si usano gli sfondi originali.
  const box = useMemo(() => (preset.id === "hostland" ? null : boxTint(preset.accent)), [preset]);
  // Header di sezione coerenti con l'accent quando il box è tintato (non-Hostland).
  const hdr = useMemo(() => (box ? { pillBg: box.border, pillText: preset.accent, titleColor: preset.accent, subtitleColor: preset.accent } : null), [box, preset]);

  const nextImage = () => { if (property?.media) setCurrentImageIndex((p) => (p + 1) % property.media.length); };
  const prevImage = () => { if (property?.media) setCurrentImageIndex((p) => (p - 1 + property.media.length) % property.media.length); };

  const hasContacts = property?.phone || property?.email || property?.whatsapp || property?.website;
  const hasOtaLinks = property?.ota_links && Object.entries(property.ota_links).some(([k, v]) => v && k !== "custom_name");

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#fff" }} data-prerender-pending="1">
      <div className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
    </div>
  );

  if (!property) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#fff" }}>
      <div className="text-center px-6">
        <h2 className="text-xl font-semibold text-zinc-900 mb-2">Proprietà non trovata</h2>
        <p className="text-zinc-500 text-sm">Questa proprietà non esiste o non è più disponibile</p>
      </div>
    </div>
  );

  const videoItems = [
    { url: property.youtube_tour_url, label: t("landing.home_tour"), key: "tour" },
    { url: property.youtube_welcome_host_url, label: t("landing.host_intro"), key: "welcome" }
  ].filter(v => v.url);

  return (
    <div className="min-h-screen" style={{ background: theme.bg, fontFamily: `'${fontFamily}', system-ui, sans-serif`, color: theme.text }}>
      {fontFamily !== "Inter" && (
        <link href={`https://fonts.googleapis.com/css2?family=${fontFamily.replace(/ /g, '+')}:wght@300;400;500;600;700&display=swap`} rel="stylesheet" />
      )}

      {/* ═══ STICKY BAR removed (was distracting on scroll) ═══ */}

      {/* Language selector floating — hidden su desktop dove ora è integrato nella action row centrata sotto il titolo */}
      <div className="fixed top-4 right-4 z-50 hidden">
        <LanguageSelector />
      </div>

      {/* ═══ AIRBNB-STYLE HERO ═══ */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-3 md:pt-6">
        {/* Title + Actions Bar — layout mobile-style anche su desktop: centrato e moderno */}
        <div className="hidden md:flex items-end justify-between gap-6 mb-4 md:mb-0 max-w-[1200px] mx-auto">
          <div className="min-w-0 text-left">
            <p className="text-[80px] font-extrabold leading-[1]" style={{ color: "#1C1C1E", letterSpacing: "-0.035em" }} data-testid="property-title-desktop">
              {translatedContent?.name || property.name}
            </p>
            <div className="mt-3 flex items-center flex-wrap gap-x-3 gap-y-2 text-[15px]">
              {property.property_type && (
                <span style={{ color: "#D6287F", fontWeight: 600 }} data-testid="property-type-desktop">
                  {PROPERTY_TYPE_LABELS[property.property_type]?.[lang] || PROPERTY_TYPE_LABELS[property.property_type]?.en || ""}
                </span>
              )}
              {property.address && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-bold" style={{ color: "#1C1C1E" }} data-testid="address-link-desktop">
                  <MapPin className="w-4 h-4" />
                  <span className="underline underline-offset-2 decoration-1">Mappa</span>
                </a>
              )}
              {property.cin && <CinBadge cin={property.cin} />}
              {host?.identity_status === "verificato" && (
                <span data-testid="host-verified-pill-wrap">
                  <HostVerifiedShield variant="pill" lang={lang}
                    name={[host?.identity_verified_first_name, host?.identity_verified_last_initial ? host.identity_verified_last_initial + "." : ""].filter(Boolean).join(" ") || host?.name}
                    verifiedAt={host?.identity_verified_at}
                    onReport={() => { setReportReason("altro"); setShowReport(true); }} />
                </span>
              )}
            </div>
            {property.vdh_chosen && (
              <a
                href="https://vitadahost.it"
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-4 rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow mx-auto max-w-md"
                data-testid="vdh-banner-desktop"
                title="Struttura selezionata dalla redazione di Vita da Host"
              >
                <img src="/assets/scelto-da-vdh.png" alt="Scelto da Vita da Host" className="w-full h-auto block" />
              </a>
            )}
          </div>
          {/* Action row centrata — pill glass-style con Other Props / Share / Save / Lingua.
              `relative z-[60]` è FONDAMENTALE per far uscire il dropdown lingue sopra la galleria hero
              (che ha un proprio stacking context via framer-motion transform). */}
          <div className="relative z-[60] shrink-0 flex items-center justify-end gap-1 rounded-full bg-white/90 backdrop-blur border border-zinc-200 shadow-sm px-2 py-1.5" data-testid="property-action-bar-desktop">
            {/* ═══ "Le nostre strutture" dropdown — visible only when the host has more than one published property. */}
            {otherProperties.length > 0 && (
              <div className="relative" ref={otherPropsMenuRef}>
                <button
                  onClick={() => setShowOtherPropsMenu((v) => !v)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold hover:bg-zinc-100 transition-colors"
                  style={{ color: theme.text }}
                  data-testid="other-props-menu-btn-desktop"
                  aria-expanded={showOtherPropsMenu}
                  aria-haspopup="menu"
                >
                  <Home className="w-4 h-4" />
                  <span>Le nostre strutture</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showOtherPropsMenu ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {showOtherPropsMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                      className="absolute right-0 top-full mt-2 w-80 max-h-[70vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-zinc-100 z-50"
                      style={{ boxShadow: "0 20px 50px -12px rgba(0,0,0,0.25)" }}
                      data-testid="other-props-menu-desktop"
                      role="menu"
                    >
                      <div className="px-4 py-3 border-b border-zinc-100">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Le nostre strutture</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Tutte le case dello stesso host — clicca per esplorarne un'altra.</p>
                      </div>
                      <div className="p-2">
                        {otherProperties.map((op) => {
                          const slug = op.custom_slug;
                          const target = slug ? `/p/${slug}` : `/property/${op.property_id}`;
                          const city = op.comune?.nome || op.address;
                          return (
                            <button
                              key={op.property_id}
                              onClick={() => { setShowOtherPropsMenu(false); window.scrollTo(0, 0); navigate(target); }}
                              className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-zinc-50 transition-colors text-left"
                              data-testid={`other-props-menu-item-${op.property_id}`}
                            >
                              <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0" style={{ background: theme.border }}>
                                {op.media?.[0]
                                  ? <img src={`${API}/files/${op.media[0]}`} alt={op.name} className="w-full h-full object-cover" />
                                  : <div className="w-full h-full flex items-center justify-center"><Home className="w-5 h-5 text-zinc-300" /></div>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-zinc-900 truncate">{op.name}</p>
                                {city && <p className="text-[11px] text-zinc-500 truncate mt-0.5 flex items-center gap-1">
                                  <MapPin className="w-3 h-3 shrink-0" /> {city}
                                </p>}
                              </div>
                              <ChevronRight className="w-4 h-4 text-zinc-300 shrink-0" />
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold hover:bg-zinc-100 transition-colors"
              style={{ color: theme.text }}
              data-testid="share-btn-desktop"
            >
              {shareCopied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              <span>{shareCopied ? "Copiato" : "Condividi"}</span>
            </button>
            <button
              onClick={handleToggleLike}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold hover:bg-zinc-100 transition-colors"
              style={{ color: theme.text }}
              data-testid="save-btn-desktop"
            >
              <Heart className="w-4 h-4 transition-colors" style={{ color: liked ? '#FF385C' : theme.text, fill: liked ? '#FF385C' : 'none' }} />
              <span>{liked ? "Salvato" : "Salva"}</span>
            </button>
            {/* Divider + Language selector inline */}
            <span aria-hidden className="w-px h-5 bg-zinc-200 mx-1" />
            <LangDropdown />
          </div>
        </div>

        {/* ═══ COVER FLOW 3D GALLERY (tech-style) ═══ */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }} className="relative mt-2 mb-5 md:mt-12 md:mb-12" data-testid="hero-cover-flow-wrapper">
          {(() => {
            const videoId = getYoutubeId(property.youtube_tour_url);
            const imageSlides = (property.media || []).map(m => ({ type: "image", src: resolveMediaUrl(m) }));
            const slides = videoId
              ? [{ type: "video", src: videoId }, ...imageSlides]
              : imageSlides;
            if (slides.length === 0) {
              return (
                <div className="relative w-full rounded-2xl flex items-center justify-center" style={{ aspectRatio: '16/10', maxHeight: '60vh', background: theme.border }} data-testid="hero-gallery">
                  <Home className="w-16 h-16 text-zinc-300" />
                </div>
              );
            }
            return (
              <CoverFlow
                slides={slides}
                currentIndex={currentImageIndex}
                setCurrentIndex={setCurrentImageIndex}
                onOpen={() => setShowAllPhotos(true)}
                accent="#FF385C"
              />
            );
          })()}

          {/* Show all photos pill (bottom-left) */}
          {property.media && property.media.length > 0 && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              onClick={() => setShowAllPhotos(true)}
              className="absolute bottom-3 left-3 md:bottom-4 md:left-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold backdrop-blur-md transition-all hover:scale-105 z-10"
              style={{ background: "rgba(255,255,255,0.92)", color: theme.text, boxShadow: "0 4px 14px -4px rgba(0,0,0,0.15)" }}
              data-testid="show-all-photos-btn"
            >
              <Grid3X3 className="w-3.5 h-3.5" />
              <span>Tutte le foto ({property.media.length})</span>
            </motion.button>
          )}

          {/* Mobile floating back button sulla galleria */}
          <button type="button" onClick={() => navigate(-1)} aria-label="Indietro" data-testid="back-btn" className="md:hidden absolute top-3 left-3 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-md z-20 active:scale-95 transition-transform" style={{ WebkitTapHighlightColor: 'transparent' }}>
            <ChevronLeft className="w-5 h-5 text-zinc-800" />
          </button>

          {/* Keep gallery-prev/gallery-next for accessibility (hidden but functional) */}
        </motion.div>

        {/* ═══ Header mobile (SOTTO la galleria) — centrato, compatto ═══ */}
        <div className="md:hidden px-6 pt-6 text-center" data-testid="property-header-mobile">
          <h1 className="text-[32px] font-extrabold leading-[1.08]" style={{ color: "#1C1C1E", letterSpacing: "-0.03em" }} data-testid="property-title">{translatedContent?.name || property.name}</h1>
          {(property.property_type || property.comune?.nome || property.address) && (
            <div className="mt-2 flex items-center justify-center flex-wrap gap-x-2 gap-y-1 text-[14px]" style={{ color: "#6B6B70" }}>
              {property.property_type && <span style={{ color: "#D6287F", fontWeight: 600 }} data-testid="property-type-mobile">{PROPERTY_TYPE_LABELS[property.property_type]?.[lang] || PROPERTY_TYPE_LABELS[property.property_type]?.en || ""}</span>}
              {property.property_type && property.comune?.nome && <span>·</span>}
              {property.comune?.nome && <span>{property.comune.nome}</span>}
              {property.address && (property.property_type || property.comune?.nome) && <span>·</span>}
              {property.address && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold underline underline-offset-2" style={{ color: "#1C1C1E" }} data-testid="address-map-link-mobile">
                  <MapPin className="w-4 h-4" /> Mappa
                </a>
              )}
            </div>
          )}
          {property.cin && <div className="mt-3 flex justify-center"><CinBadge cin={property.cin} /></div>}
          {host?.identity_status === "verificato" && (
            <div className="mt-2 flex justify-center" data-testid="host-verified-pill-wrap-mobile">
              <HostVerifiedShield variant="pill" lang={lang} name={[host?.identity_verified_first_name, host?.identity_verified_last_initial ? host.identity_verified_last_initial + "." : ""].filter(Boolean).join(" ") || host?.name} verifiedAt={host?.identity_verified_at} onReport={() => { setReportReason("altro"); setShowReport(true); }} />
            </div>
          )}

          {/* Pill "Le nostre strutture" (se ci sono altre strutture dell'host) */}
          {otherProperties.length > 0 && (
            <div className="mt-3 flex justify-center">
              <div className="relative" ref={otherPropsMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowOtherPropsMenu((v) => !v)}
                  className="inline-flex items-center gap-2 pl-3 pr-3.5 h-10 rounded-full text-[13px] font-semibold active:scale-95 transition-transform"
                  style={{ border: "1px solid #ECECEE", background: "#fff", color: "#1C1C1E" }}
                  data-testid="other-props-menu-btn-mobile"
                  aria-expanded={showOtherPropsMenu}
                >
                  <Home className="w-4 h-4" />
                  Le nostre strutture
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showOtherPropsMenu ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {showOtherPropsMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                      className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[min(18rem,calc(100vw-3rem))] max-h-[60vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-zinc-100 z-50 text-left"
                      style={{ boxShadow: "0 20px 50px -12px rgba(0,0,0,0.3)" }}
                      data-testid="other-props-menu-mobile"
                    >
                      <div className="px-4 py-3 border-b border-zinc-100">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Le nostre strutture</p>
                      </div>
                      <div className="p-2">
                        {otherProperties.map((op) => {
                          const slug = op.custom_slug;
                          const target = slug ? `/p/${slug}` : `/property/${op.property_id}`;
                          const city = op.comune?.nome || op.address;
                          return (
                            <button
                              key={op.property_id}
                              onClick={() => { setShowOtherPropsMenu(false); window.scrollTo(0, 0); navigate(target); }}
                              className="w-full flex items-center gap-3 p-2 rounded-xl active:bg-zinc-100 transition-colors text-left"
                              data-testid={`other-props-menu-mobile-item-${op.property_id}`}
                            >
                              <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0" style={{ background: theme.border }}>
                                {op.media?.[0]
                                  ? <img src={`${API}/files/${op.media[0]}`} alt={op.name} className="w-full h-full object-cover" />
                                  : <div className="w-full h-full flex items-center justify-center"><Home className="w-4 h-4 text-zinc-300" /></div>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-zinc-900 truncate">{op.name}</p>
                                {city && <p className="text-[11px] text-zinc-500 truncate mt-0.5">{city}</p>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}

          <div className="mt-6 border-t" style={{ borderColor: "#F0F0F1" }} />
        </div>
      </div>

      {/* Content — wider on desktop */}
      <main
        className="max-w-2xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl mx-auto px-4 md:px-8 lg:px-10 pb-[calc(env(safe-area-inset-bottom,0px)+7.5rem)] md:pb-20"
        // Reserve space at the bottom on mobile equal to the sticky action
        // bar height (~92 px) + iOS safe-area, so the last content isn't
        // hidden behind the fixed bar. Using calc(env()) instead of the
        // previous `pb-32` prevents the giant white gap that appeared on
        // iOS Safari with the URL bar docked to the bottom.
      >

        {/* Scheda host (mobile, sotto la galleria) */}
        <div className="md:hidden mt-5" data-testid="property-info">
          {property.vdh_chosen && (
            <a
              href="https://vitadahost.it"
              target="_blank"
              rel="noopener noreferrer"
              className="block -mx-4 sm:mx-0 sm:rounded-2xl overflow-hidden shadow-md mb-3"
              data-testid="vdh-banner-mobile"
              title="Struttura selezionata dalla redazione di Vita da Host"
            >
              <img src="/assets/scelto-da-vdh.png" alt="Scelto da Vita da Host" className="w-full h-auto block" />
            </a>
          )}
          <div className="flex items-center gap-3 rounded-[18px] p-4" style={{ border: "1px solid #ECECEE" }}>
            <button onClick={() => setShowHostProfile(true)} className="flex items-center gap-3 min-w-0 flex-1 text-left" data-testid="host-link">
              {host?.avatar_url ? (
                <img src={host.avatar_url.startsWith("http") ? host.avatar_url : `${API}/files/${host.avatar_url}`} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold shrink-0" style={{ background: "#F3F3F4", color: "#6B6B70" }}>{host?.name?.charAt(0)}</div>
              )}
              <span className="min-w-0">
                <span className="block text-[11px] uppercase tracking-wider" style={{ color: "#6B6B70" }}>Il tuo host</span>
                <span className="block text-[15px] font-bold truncate" style={{ color: "#1C1C1E" }}>{host?.name}</span>
                {host?.created_at && <span className="block text-[12px]" style={{ color: "#6B6B70" }} data-testid="host-seniority-mobile">{formatHostSeniority(host.created_at, t)}</span>}
              </span>
            </button>
            <SocialRowMono property={property} />
          </div>
        </div>

        {/* Host strip — desktop (after gallery, Airbnb style).
            NOTE: no borderBottom here; the section is closed by the thicker
            <Divider> below, avoiding a double-line effect. */}
        <div className="hidden md:flex items-center justify-between gap-4 mt-6 pt-6" style={{ borderTop: "1px solid #ECECEE" }}>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowHostProfile(true)} className="inline-flex items-center gap-3 hover:opacity-70 transition-opacity" data-testid="host-link-desktop">
              {host?.avatar_url ? (
                <img src={host.avatar_url.startsWith('http') ? host.avatar_url : `${API}/files/${host.avatar_url}`} alt="" className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-base font-bold" style={{ background: "#F3F3F4", color: "#6B6B70" }}>{host?.name?.charAt(0)}</div>
              )}
              <span className="text-left">
                <span className="block text-[11px] uppercase tracking-wider" style={{ color: "#6B6B70" }}>Il tuo host</span>
                <span className="block text-[17px] font-bold tracking-tight" style={{ color: "#1C1C1E" }}>{host?.name}</span>
                {host?.created_at && (
                  <span className="block text-[12px] font-medium mt-0.5" style={{ color: "#6B6B70" }} data-testid="host-seniority-desktop">
                    {formatHostSeniority(host.created_at, t)}
                  </span>
                )}
              </span>
            </button>
            {host?.identity_status === "verificato" && (
              <HostVerifiedShield
                name={[host?.identity_verified_first_name, host?.identity_verified_last_initial ? host.identity_verified_last_initial + "." : ""].filter(Boolean).join(" ") || host?.name}
                verifiedAt={host?.identity_verified_at} lang={lang} size={22}
                onReport={() => { setReportReason("altro"); setShowReport(true); }}
              />
            )}
          </div>
          <SocialRowMono property={property} suffix="-desktop" />
        </div>

        {/* Booking Engine — from Channel Manager.
            Hosts can paste either the FULL HTML embed (iframe/script) provided by their
            channel manager OR a simple URL (e.g. https://siteminder.com/property/123).
            When it's just a URL we render a prominent CTA button instead of trying to
            embed something that isn't HTML. */}
        {/* ═══ BOOKING ENGINE EMBED ═══
            Renders either an iframe/HTML embed (e.g. channel manager) OR a friendly
            CTA button when the host pasted just a URL.
            Fallback chain: booking_engine_html → channel_widget_html (legacy field) —
            so hosts that filled only the OLD "Disponibilità & Prenotazioni" field
            still get the new CTA on their landing without re-typing anything. */}
        {(property.booking_engine_html || property.channel_widget_html) && (() => {
          const raw = String(property.booking_engine_html || property.channel_widget_html || "").trim();
          // Hosts may paste either FULL HTML (iframe/script/<div…>) OR just a URL.
          // We treat the input as a plain URL when it has no HTML tag, no whitespace
          // (HTML embeds always span multiple lines / contain spaces), and either
          // starts with http(s)://, www. or a bare "domain.tld" pattern.
          const hasHtml = /<\w+[^>]*>/.test(raw);
          const hasWhitespace = /\s/.test(raw);
          const looksLikeUrl = !hasHtml && !hasWhitespace && /^(https?:\/\/|www\.|([a-z0-9-]+\.)+[a-z]{2,}([\/:?#]|$))/i.test(raw);
          const normalizedUrl = looksLikeUrl
            ? (/^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, "")}`)
            : null;
          return (
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={1.5}
              className="mt-4 md:mt-6 w-full max-w-2xl mx-auto"
              data-testid="booking-engine-embed"
            >
              {looksLikeUrl ? (
                <a
                  href={normalizedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center rounded-2xl bg-gradient-to-r from-rose-600 via-pink-600 to-rose-600 hover:from-rose-700 hover:via-pink-700 hover:to-rose-700 text-white py-5 md:py-6 font-black text-base md:text-lg tracking-tight shadow-2xl shadow-rose-500/30 transition-all hover:scale-[1.01] active:scale-[0.99]"
                  data-testid="booking-engine-link-cta"
                >
                  🗓 Prenotazione diretta aggiornata in tempo reale
                </a>
              ) : (
                <div
                  className="rounded-2xl overflow-hidden [&>iframe]:!w-full [&>iframe]:!max-w-full [&>iframe]:rounded-xl [&>div]:!w-full [&>div]:!max-w-full [&>script+div]:!w-full"
                  style={{ width: "100%" }}
                  dangerouslySetInnerHTML={{ __html: raw }}
                />
              )}
            </motion.div>
          );
        })()}

        <Divider theme={theme} thick />

        {/* Eventi in zona — box SEPARATA (marquee infinito), posizionata tra la
            riga sotto il nome host e la box "Informazioni generali". Nessun
            header: si capisce dai contenuti. Ritorna null se non ci sono eventi. */}
        <EventsTicker
          apiBaseUrl={BACKEND_URL}
          city={typeof property.comune === "string" ? property.comune : (property.comune?.nome || "")}
          propertyId={property.property_id || property.id || ""}
          theme={theme}
        />

        {/* ═══════════════════════════════════════════════════════════════
            HERO STATS & UPDATES — Unified container
            Wraps capacity chips + KPI cards (Occupazione/Visualizzazioni/
            Meteo) + Aggiornamenti host + Eventi RSS in a single editorial
            box. Coherent with "Come prenotare" and "La Casa". One subtle
            gradient bg + decorative orbs + section headers. Everything inside
            keeps its own visual identity but lives in the same canvas. */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mt-5 md:mt-6 rounded-3xl p-5 md:p-8 lg:p-10 relative overflow-hidden"
          style={{ background: box ? box.bg : "linear-gradient(180deg, #FAFAFB 0%, #F4F4F5 100%)", border: `1px solid ${box ? box.border : "#EBEBEB"}` }}
          data-testid="hero-stats-box"
        >
          {/* Soft decorative orbs */}
          <motion.div
            animate={{ x: [0, 30, 0], y: [0, -20, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: box ? box.orb1 : "radial-gradient(circle, rgba(255,56,92,0.08), transparent 70%)" }}
          />
          <motion.div
            animate={{ x: [0, -25, 0], y: [0, 20, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 16, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: box ? box.orb2 : "radial-gradient(circle, rgba(59,130,246,0.06), transparent 70%)" }}
          />

          <div className="relative">
            {/* Section header — "Informazioni generali" */}
            <SectionHeader
              icon={BarChart3}
              eyebrow={t("sec.info.eyebrow")}
              title={t("sec.info.title")}
              subtitle={t("sec.info.subtitle")}
              pillBg="rgba(15,23,42,0.06)"
              pillText="#0F172A"
              titleColor="#0F172A"
              subtitleColor="#52525B"
              testId="sec-info-header"
            />

            {/* Capacity chips with icons */}
            <StatsPillRow
              stats={[
                { icon: Users, val: property.guests_count, label: t("landing.guests") },
                { icon: Bed, val: property.rooms_count, label: t("landing.rooms") },
                { icon: Bath, val: property.bathrooms_count, label: t("landing.bathrooms") },
                ...(property.sqft ? [{ icon: Square, val: property.sqft, label: "m²" }] : [])
              ]}
              theme={theme}
              accent="#FF385C"
            />

            {/* KPI cards */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-3"
              data-testid="kpi-cards-grid"
            >
              {/* Mobile: Occupancy + Views come due quadrati affiancati.
                  Desktop: md:contents li appiattisce nel grid a 3 colonne. */}
              <div className="grid grid-cols-2 gap-3 md:contents">
                <OccupancyCard value={occupancyPct} theme={theme} />
                <ViewsCard value={property.qr_scans || property.unique_scans || 0} theme={theme} />
              </div>
              <WeatherWeekCard weather={weather} theme={theme} />
            </motion.div>

            {/* Condizioni di soggiorno — box unica (solo campi compilati) */}
            <StayConditions property={property} theme={theme} />

          </div>
        </motion.div>

        {/* Aggiornamenti host — SEZIONE AUTONOMA (fuori dal box Informazioni; appare solo se ci sono articoli) */}
        {blogPosts.length > 0 && (
          <div className="mt-6 md:mt-8" data-testid="blog-section">
            <BlogNewsBox
              posts={blogPosts}
              apiUrl={API}
              getYouTubeId={getYouTubeId}
              theme={theme}
              translations={{}}
            />
          </div>
        )}

        {/* Videos — full section box in stile "Informazioni generali" / "La struttura",
            palette amber. Sostituisce anche il vecchio Divider che era tra Eventi e Home Tour. */}
        {videoItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="mt-5 md:mt-6 rounded-3xl p-5 md:p-8 lg:p-10 relative overflow-hidden"
            style={{ background: box ? box.bg : "linear-gradient(180deg, #FFFBEB 0%, #FEF3C7 100%)", border: `1px solid ${box ? box.border : "#FDE68A"}` }}
            data-testid="video-section-box"
          >
            {/* Soft decorative orbs — amber palette */}
            <motion.div
              animate={{ x: [0, 30, 0], y: [0, -20, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: box ? box.orb1 : "radial-gradient(circle, rgba(245,158,11,0.14), transparent 70%)" }}
            />
            <motion.div
              animate={{ x: [0, -25, 0], y: [0, 20, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 16, repeat: Infinity, ease: "easeInOut", delay: 2 }}
              className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: box ? box.orb2 : "radial-gradient(circle, rgba(217,119,6,0.10), transparent 70%)" }}
            />

            <div className="relative">
              {/* Section header — "Video è meglio!" (stesso pattern dei blocchi sopra/sotto) */}
              <SectionHeader
                icon={Play}
                eyebrow={t("sec.video.eyebrow")}
                title={t("sec.video.title")}
                subtitle={t("sec.video.subtitle")}
                pillBg={hdr ? hdr.pillBg : "rgba(180,83,9,0.14)"}
                pillText={hdr ? hdr.pillText : "#78350F"}
                titleColor={hdr ? hdr.titleColor : "#78350F"}
                subtitleColor={hdr ? hdr.subtitleColor : "#B45309"}
                testId="sec-video-header"
              />

              <div className={`space-y-6 ${videoItems.length === 2 ? 'md:space-y-0 md:grid md:grid-cols-2 md:gap-5' : ''}`}>
                {videoItems.map(({ url, label, key }) => {
                  const videoId = getYouTubeId(url);
                  const thumb = getYouTubeThumbnail(url);
                  const isPlaying = playingVideo === key;
                  // Detect a direct video file (self-hosted mp4/webm/mov) — we render
                  // it with the native <video> tag instead of trying to embed as
                  // YouTube. Used for the AI-generated demo tour/welcome clips.
                  const isDirectVideo = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
                  // Original YouTube URL — used as a graceful fallback when the
                  // embedded player can't play the video (e.g. uploader disabled
                  // embedding or the video is region-locked). Always shown as a
                  // tiny "Apri su YouTube" link below the player so guests are
                  // never stuck staring at the YouTube error screen.
                  const watchUrl = videoId ? `https://www.youtube.com/watch?v=${videoId}` : url;
                  return (
                    <div key={key} data-testid={`video-embed-${key}`}>
                      <p className="text-center text-xs font-semibold uppercase tracking-[0.15em] mb-3 inline-flex items-center gap-1.5 justify-center w-full" style={{ color: theme.sub }}>
                        {key === "tour" ? <Home className="w-3 h-3" strokeWidth={2.4} /> : <UserPlus className="w-3 h-3" strokeWidth={2.4} />}
                        {label}
                      </p>
                      <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${theme.border}` }}>
                        <div className="relative aspect-video bg-black">
                          {isDirectVideo ? (
                            <video
                              src={url}
                              poster={(property.media && property.media[0]) || undefined}
                              className="w-full h-full object-cover"
                              controls
                              playsInline
                              preload="auto"
                              data-testid={`video-native-${key}`}
                            />
                          ) : isPlaying && videoId ? (
                            <iframe
                              src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1&modestbranding=1`}
                              className="w-full h-full"
                              allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                              allowFullScreen
                              title={label}
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                            />
                          ) : videoId ? (
                            <button onClick={() => setPlayingVideo(key)} className="w-full h-full relative group cursor-pointer" aria-label={`Riproduci ${label}`}>
                              <img
                                src={thumb}
                                alt={label}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  // Fallback chain: hqdefault → mqdefault → default.jpg → gradient
                                  const img = e.currentTarget;
                                  const src = img.src;
                                  if (src.includes("hqdefault")) {
                                    img.src = `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
                                  } else if (src.includes("mqdefault")) {
                                    img.src = `https://i.ytimg.com/vi/${videoId}/default.jpg`;
                                  } else {
                                    img.style.display = "none";
                                    // Set gradient fallback on the container
                                    const parent = img.closest("button");
                                    if (parent) parent.style.background = "linear-gradient(135deg, #1f2937, #0f172a)";
                                  }
                                }}
                              />
                              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                  <Play className="w-5 h-5 md:w-6 md:h-6 ml-0.5 text-zinc-900" fill="#222" />
                                </div>
                              </div>
                            </button>
                          ) : (
                            // Fallback ultimo: URL YouTube non riconosciuto dal parser
                            // (es. link livestream con formato inatteso). Mostriamo un
                            // CTA chiaro invece di un box nero muto.
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-950 hover:from-zinc-700 hover:to-zinc-900 transition-colors group text-white"
                              data-testid={`video-fallback-external-${key}`}
                            >
                              <div className="text-center px-6">
                                <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-white/20 transition-colors">
                                  <Play className="w-6 h-6 text-white ml-0.5" fill="currentColor" />
                                </div>
                                <p className="text-sm font-bold">Apri su YouTube</p>
                                <p className="text-[11px] opacity-60 mt-1">Il video ha un formato URL personalizzato</p>
                              </div>
                            </a>
                          )}
                        </div>
                      </div>
                      {/* "Apri su YouTube" — sempre visibile, salva l'ospite quando l'embed è bloccato.
                          Nascosto quando il video è un file diretto (mp4/webm) che si autoservie. */}
                      {!isDirectVideo && (
                        <div className="mt-2 text-center">
                          <a
                            href={watchUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] font-semibold hover:underline"
                            style={{ color: theme.sub }}
                            data-testid={`video-open-youtube-${key}`}
                          >
                            <Play className="w-3 h-3" fill="currentColor" />
                            {t("news.video_fallback")}
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </motion.div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            LA CASA & SERVIZI — Unified container
            Merges PropertyDescription ("La Casa") and the amenities treemap
            into one editorial box, consistent with the hero stats container
            above. Decorative blobs, soft gradient bg, and inline section
            headers preserve a single coherent canvas for the guest. */}
        {(property.description || (property.amenities && property.amenities.length > 0) || (property.location_lat && property.location_lng) || (Array.isArray(property.poi_list) && property.poi_list.length > 0)) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="mt-5 md:mt-6 rounded-3xl p-5 md:p-8 lg:p-10 relative overflow-hidden"
            style={{ background: box ? box.bg : "linear-gradient(180deg, #F0FDF4 0%, #ECFDF5 100%)", border: `1px solid ${box ? box.border : "#BBF7D0"}` }}
            data-testid="la-casa-box"
          >
            {/* Decorative orbs — soft mint-green tones */}
            <motion.div
              animate={{ x: [0, 25, 0], y: [0, -15, 0], scale: [1, 1.08, 1] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: box ? box.orb1 : "radial-gradient(circle, rgba(16,185,129,0.10), transparent 70%)" }}
            />
            <motion.div
              animate={{ x: [0, -20, 0], y: [0, 20, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 16, repeat: Infinity, ease: "easeInOut", delay: 2 }}
              className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: box ? box.orb2 : "radial-gradient(circle, rgba(59,130,246,0.07), transparent 70%)" }}
            />

            <div className="relative">
              {/* Section header — "La struttura" */}
              <SectionHeader
                icon={Home}
                eyebrow={t("sec.house.eyebrow")}
                title={t("sec.house.title")}
                subtitle={t("sec.house.subtitle")}
                pillBg={hdr ? hdr.pillBg : "rgba(16,185,129,0.14)"}
                pillText={hdr ? hdr.pillText : "#065F46"}
                titleColor={hdr ? hdr.titleColor : "#064E3B"}
                subtitleColor={hdr ? hdr.subtitleColor : "#047857"}
                testId="sec-house-header"
              />

              {/* La Casa (description) */}
              {property.description && (
                <PropertyDescription
                  text={translatedContent?.description || property.description}
                  theme={theme}
                  title={t("pl.house_title")}
                />
              )}

              {/* Mappa "Dove ti troverai" — apre modal in-page (no cambio finestra).
                  Design: OpenStreetMap embed con marker nativo (verde), overlay
                  address + CTA in basso. Il pulse rosso animato precedente è
                  stato rimosso (alleggerimento sistema + il marker OSM è già
                  chiaramente visibile). Il container ha aspect-ratio nativo per
                  evitare il "half-load" su desktop dove il resize del thumb map
                  arrivava dopo il primo paint. */}
              {property.location_lat && property.location_lng && (
                <motion.button
                  type="button"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5 }}
                  onClick={() => setMapModalOpen(true)}
                  className={`${property.description ? "mt-6 md:mt-8" : ""} block w-full rounded-2xl overflow-hidden relative group cursor-pointer text-left`}
                  style={{ background: "#e5e7eb", aspectRatio: "16 / 9", maxHeight: 420 }}
                  data-testid="map-widget"
                >
                  <iframe
                    title="Mappa"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${property.location_lng - 0.02},${property.location_lat - 0.014},${property.location_lng + 0.02},${property.location_lat + 0.014}&layer=mapnik&marker=${property.location_lat},${property.location_lng}`}
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    style={{ border: 0 }}
                    loading="lazy"
                  />
                  {/* Address label + CTA overlay in basso */}
                  <div className="absolute bottom-0 inset-x-0 p-3.5 md:p-4 bg-gradient-to-t from-black/70 via-black/35 to-transparent pointer-events-none">
                    <p className="text-[9px] md:text-[10px] font-bold uppercase tracking-[0.18em] text-white/85">Dove ti troverai</p>
                    {property.address && (
                      <p className="text-white text-sm md:text-base font-bold mt-0.5 tracking-tight truncate" style={{ letterSpacing: "-0.01em" }}>
                        {property.address}
                      </p>
                    )}
                    <span
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold backdrop-blur-xl pointer-events-none"
                      style={{ background: "rgba(255,255,255,0.95)", color: "#18181B", boxShadow: "0 6px 18px -4px rgba(0,0,0,0.35)" }}
                      data-testid="map-cta"
                    >
                      <MapPin className="w-3.5 h-3.5" style={{ color: "#FF385C" }} />
                      Espandi mappa
                    </span>
                  </div>
                </motion.button>
              )}

              {/* Punti di Interesse — lista minimal sotto la mappa.
                  I nomi vengono tradotti nel bg via Gemini (backend endpoint
                  /properties/{id}/translations/{lang}) e cachati in DB. */}
              <PropertyPOIList
                pois={property.poi_list || []}
                translations={translatedContent?.pois || {}}
                lang={lang}
                theme={theme}
              />

              {/* Amenities treemap — header ("COSA TROVERAI · N servizi" + subtitle)
                  è ora renderizzato internamente da AmenitiesTreemap con stile coerente
                  al POI list. Nessun eyebrow esterno per evitare duplicazione. */}
              {property.amenities && property.amenities.length > 0 && (
                <div className={(property.description || (property.location_lat && property.location_lng)) ? "mt-6 md:mt-8" : ""} data-testid="amenities-display">
                  <AmenitiesTreemap amenityIds={property.amenities} amenitiesData={AMENITIES_DATA} theme={theme} fullWidth paletteColors={preset.id !== "hostland" ? preset.buttons : null} />
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Reviews, PublicAvailability e Weather/Map ora sono integrati
            rispettivamente nella box "La Casa" (Mappa) e nella box rossa
            "Come prenotare" (Calendario + Recensioni). */}

        {/* ═══════════════════════════════════════════════════════════════
            COME PRENOTARE — Unified premium section (RED gradient)
            Box rossa unificata che raccoglie tutto ciò che serve all'ospite:
            1) Calendario disponibilità (in cima)
            2) Contatti diretti (call/WhatsApp/email/website)
            3) OTA platforms (Airbnb/Booking/Vrbo)
            4) Recensioni Google (in fondo, slider coerente)
            ═══════════════════════════════════════════════════════════════ */}
        {(hasContacts || hasOtaLinks || reviews.length > 0 || property) && (
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={6}
            data-testid="how-to-book"
            className="mt-5 md:mt-6 rounded-3xl p-6 md:p-10 relative overflow-hidden"
            style={{ background: "linear-gradient(160deg, #FFF1F2 0%, #FFE4E6 45%, #FECDD3 100%)", border: "1px solid #FBCFE0" }}
          >
            {/* Decorative animated background blobs — red/rose tones */}
            <motion.div
              animate={{ x: [0, 40, 0], y: [0, -30, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(244,63,94,0.18), transparent 70%)" }}
            />
            <motion.div
              animate={{ x: [0, -35, 0], y: [0, 25, 0], scale: [1, 1.15, 1] }}
              transition={{ duration: 16, repeat: Infinity, ease: "easeInOut", delay: 2 }}
              className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(225,29,72,0.14), transparent 70%)" }}
            />

            {/* Section header */}
            <div className="relative text-center mb-6 md:mb-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-3"
                style={{ background: "rgba(225,29,72,0.14)", color: "#BE123C" }}
              >
                <Send className="w-3 h-3" /> {t("htb.eyebrow")}
              </motion.div>
              <h2 className="text-3xl md:text-5xl font-black tracking-tight" style={{ color: "#7F1D1D" }}>
                {t("htb.title")}
              </h2>
              <p className="text-[15px] md:text-lg mt-2.5 max-w-md mx-auto" style={{ color: "#9F1239" }}>
                {t("htb.subtitle")}
              </p>
            </div>

            {/* CALENDARIO disponibilità — primo elemento della box prenotazione.
                Wrappato in un container glassmorphism bianco che lo separa
                visivamente dal gradiente rosso e mantiene leggibilità. */}
            <div className="relative mb-6 md:mb-8">
              <div className="rounded-3xl p-4 md:p-6 backdrop-blur-xl" style={{ background: "rgba(255,255,255,0.85)", border: "1px solid rgba(244,63,94,0.15)", boxShadow: "0 10px 30px -12px rgba(244,63,94,0.20)" }} data-testid="availability-in-booking-box">
                <PublicAvailability property={property} theme={theme} inline />
              </div>
            </div>

            {/* DIRECT contact tiles */}
            {hasContacts && (
              <div className="relative">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: "#9F1239" }}>
                  {t("htb.direct")}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  {property.phone && (
                    <motion.a
                      whileHover={{ y: -5, scale: 1.02 }} whileTap={{ scale: 0.97 }}
                      href={`tel:${property.phone}`}
                      className="group relative overflow-hidden rounded-3xl p-5 md:p-6 flex flex-col items-center justify-center gap-3 text-center transition-shadow hover:shadow-2xl min-h-[140px] md:min-h-[160px]"
                      style={{ background: "linear-gradient(135deg, #FF385C 0%, #E61E4D 100%)", color: "#FFFFFF", boxShadow: "0 10px 30px -10px rgba(255,56,92,0.55)" }}
                      data-testid="contact-phone"
                    >
                      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none transition-transform group-hover:scale-150" style={{ background: "rgba(255,255,255,0.18)" }} />
                      <div className="absolute -bottom-8 -left-8 w-20 h-20 rounded-full pointer-events-none" style={{ background: "rgba(255,255,255,0.08)" }} />
                      <motion.div
                        animate={{ rotate: [0, -8, 8, -8, 0] }}
                        transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 4 }}
                        className="w-12 h-12 rounded-2xl flex items-center justify-center relative"
                        style={{ background: "rgba(255,255,255,0.22)" }}
                      >
                        <Phone className="w-6 h-6" />
                      </motion.div>
                      <div className="relative">
                        <p className="text-[10px] font-black uppercase tracking-widest opacity-85 mb-0.5">{t("htb.call")}</p>
                        <p className="text-sm font-bold leading-tight truncate max-w-[180px]">{t("htb.tap_to_call")}</p>
                      </div>
                    </motion.a>
                  )}
                  {property.whatsapp && (
                    <motion.a
                      whileHover={{ y: -5, scale: 1.02 }} whileTap={{ scale: 0.97 }}
                      href={`https://wa.me/${property.whatsapp.replace(/[^0-9+]/g, '')}`} target="_blank" rel="noopener noreferrer"
                      className="group relative overflow-hidden rounded-3xl p-5 md:p-6 flex flex-col items-center justify-center gap-3 text-center transition-shadow hover:shadow-2xl min-h-[140px] md:min-h-[160px]"
                      style={{ background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)", color: "#FFFFFF", boxShadow: "0 10px 30px -10px rgba(37,211,102,0.55)" }}
                      data-testid="contact-whatsapp"
                    >
                      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none transition-transform group-hover:scale-150" style={{ background: "rgba(255,255,255,0.18)" }} />
                      <div className="absolute -bottom-8 -left-8 w-20 h-20 rounded-full pointer-events-none" style={{ background: "rgba(255,255,255,0.08)" }} />
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="w-12 h-12 rounded-2xl flex items-center justify-center relative"
                        style={{ background: "rgba(255,255,255,0.22)" }}
                      >
                        <MessageCircle className="w-6 h-6" />
                      </motion.div>
                      <div className="relative">
                        <p className="text-[10px] font-black uppercase tracking-widest opacity-85 mb-0.5">{t("htb.chat")}</p>
                        <p className="text-sm font-bold leading-tight">WhatsApp</p>
                      </div>
                    </motion.a>
                  )}
                  {property.email && (
                    <motion.button
                      type="button"
                      whileHover={{ y: -5, scale: 1.02 }} whileTap={{ scale: 0.97 }}
                      onClick={() => { window.location.href = `mailto:${property.email}?subject=${encodeURIComponent("Richiesta info " + (property.name || ""))}`; }}
                      className="group relative overflow-hidden rounded-3xl p-5 md:p-6 flex flex-col items-center justify-center gap-3 text-center transition-shadow hover:shadow-2xl min-h-[140px] md:min-h-[160px]"
                      style={{ background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)", color: "#FFFFFF", boxShadow: "0 10px 30px -10px rgba(59,130,246,0.55)" }}
                      data-testid="contact-email"
                    >
                      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none transition-transform group-hover:scale-150" style={{ background: "rgba(255,255,255,0.18)" }} />
                      <div className="absolute -bottom-8 -left-8 w-20 h-20 rounded-full pointer-events-none" style={{ background: "rgba(255,255,255,0.08)" }} />
                      <motion.div
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                        className="w-12 h-12 rounded-2xl flex items-center justify-center relative"
                        style={{ background: "rgba(255,255,255,0.22)" }}
                      >
                        <Mail className="w-6 h-6" />
                      </motion.div>
                      <div className="relative">
                        <p className="text-[10px] font-black uppercase tracking-widest opacity-85 mb-0.5">Email</p>
                        <p className="text-xs font-bold leading-tight">{t("htb.tap_to_email") || "Tocca per scrivere"}</p>
                      </div>
                    </motion.button>
                  )}
                  {property.website && (
                    <motion.a
                      whileHover={{ y: -5, scale: 1.02 }} whileTap={{ scale: 0.97 }}
                      href={property.website} target="_blank" rel="noopener noreferrer"
                      className="group relative overflow-hidden rounded-3xl p-5 md:p-6 flex flex-col items-center justify-center gap-3 text-center transition-shadow hover:shadow-2xl min-h-[140px] md:min-h-[160px]"
                      style={{ background: "linear-gradient(135deg, #18181B 0%, #3F3F46 100%)", color: "#FFFFFF", boxShadow: "0 10px 30px -10px rgba(0,0,0,0.4)" }}
                      data-testid="contact-website"
                    >
                      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none transition-transform group-hover:scale-150" style={{ background: "rgba(255,255,255,0.12)" }} />
                      <div className="absolute -bottom-8 -left-8 w-20 h-20 rounded-full pointer-events-none" style={{ background: "rgba(255,255,255,0.05)" }} />
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        className="w-12 h-12 rounded-2xl flex items-center justify-center relative"
                        style={{ background: "rgba(255,255,255,0.18)" }}
                      >
                        <Globe className="w-6 h-6" />
                      </motion.div>
                      <div className="relative">
                        <p className="text-[10px] font-black uppercase tracking-widest opacity-85 mb-0.5">Web</p>
                        <p className="text-sm font-bold leading-tight">{t("htb.official")}</p>
                      </div>
                    </motion.a>
                  )}
                </div>
              </div>
            )}

            {/* OTA platform tiles (Airbnb / Booking / Vrbo / custom) */}
            {hasOtaLinks && (
              <div className="relative mt-7 md:mt-10">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "#9F1239" }}>
                    {t("htb.platforms")}
                  </p>
                  <p className="text-[10px] italic" style={{ color: "#9F1239" }}>
                    {t("htb.platforms_hint")}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-testid="booking-hub">
                  {Object.entries(property.ota_links || {}).map(([key, url]) => {
                    if (!url || key === "custom_name") return null;
                    const platform = OTA_ICONS[key] || OTA_ICONS.custom;
                    const displayName = (key === "custom" && property.ota_links?.custom_name) ? property.ota_links.custom_name : platform.name;
                    return (
                      <motion.a
                        key={key}
                        href={url} target="_blank" rel="noopener noreferrer"
                        whileHover={{ y: -5, scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                        className="group relative overflow-hidden rounded-3xl p-5 md:p-6 transition-shadow hover:shadow-2xl min-h-[120px]"
                        style={{ background: platform.bg, color: platform.fg, boxShadow: `0 12px 32px -10px ${platform.bg}99` }}
                        data-testid={`booking-${key}`}
                      >
                        {/* Shine sweep on hover */}
                        <div
                          className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{
                            background: "linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.35) 50%, transparent 65%)",
                            backgroundSize: "200% 100%",
                            animation: "shine-sweep 1.2s ease-out",
                          }}
                        />
                        {/* Decoration blobs */}
                        <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full pointer-events-none transition-transform group-hover:scale-125" style={{ background: "rgba(255,255,255,0.16)" }} />
                        <div className="absolute -bottom-10 -left-10 w-24 h-24 rounded-full pointer-events-none" style={{ background: "rgba(255,255,255,0.06)" }} />

                        <div className="relative flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 md:gap-4 min-w-0">
                            {/* OTA logo tile — uses the official brand asset when available,
                                falls back to a textual glyph for custom direct-booking links. */}
                            <motion.div
                              whileHover={{ rotate: [0, -6, 6, 0] }}
                              transition={{ duration: 0.5 }}
                              className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center shrink-0 overflow-hidden"
                              style={{ background: "#FFFFFF", boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}
                            >
                              {platform.logo ? (
                                <img
                                  src={platform.logo}
                                  alt={displayName}
                                  className="w-10 h-10 md:w-12 md:h-12 object-contain"
                                  loading="lazy"
                                />
                              ) : (
                                <span className="text-2xl font-black" style={{ color: platform.bg }}>{platform.icon}</span>
                              )}
                            </motion.div>
                            <div className="flex flex-col min-w-0">
                              <span className="text-[10px] font-black uppercase tracking-widest opacity-80">{t("pl.book_on")}</span>
                              <span className="text-base md:text-lg font-black leading-tight truncate">{displayName}</span>
                            </div>
                          </div>
                          <motion.div
                            animate={{ x: [0, 4, 0] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                            className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ background: "rgba(255,255,255,0.18)" }}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </motion.div>
                        </div>
                      </motion.a>
                    );
                  })}
                </div>
                <style>{`@keyframes shine-sweep { from { background-position: 200% 0; } to { background-position: -100% 0; } }`}</style>
              </div>
            )}

          </motion.div>
        )}

        {/* Box UNICA: Voci della Casa + Recensioni Google (disclaimer in fondo solo se ci sono voci) */}
        <HouseVoicesReviews
          propertyId={propertyId}
          accent={preset.accent}
          lang={lang}
          fontFamily={fontFamily}
          google={{
            reviews,
            rating: property.google_rating,
            reviewCount: property.google_review_count,
            lastUpdate: property.reviews_last_fetched,
            googleBusinessUrl: property.google_business_url,
          }}
        />

        {/* ═══ Perché visitare {città} — guida generata da Gemini (SEO, contenuto unico, multilingua) ═══ */}
        {property.city_guide && (
          <CityGuide
            guide={translatedContent?.city_guide || property.city_guide}
            cityName={typeof property.comune === "string" ? property.comune : (property.comune?.nome || "")}
            accent={preset.accent}
            fontFamily={fontFamily}
          />
        )}

        {/* Chat CTA — Magnetic + animated gradient bg */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={8}
          className="mt-5 md:mt-6 rounded-3xl p-6 md:p-10 text-center relative overflow-hidden" style={{ background: "#FAFAFB", border: `1px solid ${theme.border}` }}>
          {/* Animated background blobs */}
          <motion.div
            animate={{ x: [0, 30, 0], y: [0, -20, 0], scale: [1, 1.15, 1] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-10 -right-10 w-44 h-44 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(255,56,92,0.15), transparent 70%)" }}
          />
          <motion.div
            animate={{ x: [0, -25, 0], y: [0, 18, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
            className="absolute -bottom-10 -left-10 w-44 h-44 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(59,130,246,0.12), transparent 70%)" }}
          />

          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: theme.sub }}>{t("landing.questions")}</p>
            <p className="text-base md:text-2xl font-bold tracking-tight mb-5 max-w-md mx-auto leading-tight" style={{ color: theme.text }}>
              {t("pl.reply_minutes")}
            </p>
            <MagneticButton
              onClick={() => setShowContactPopup(true)}
              className="inline-flex items-center gap-2 px-7 md:px-9 py-3.5 md:py-4 rounded-full text-sm md:text-base font-bold transition-shadow hover:shadow-xl"
              style={{ background: "#FF385C", color: "#FFFFFF", boxShadow: "0 12px 28px -8px rgba(255,56,92,0.45)" }}
              testId="start-chat-btn"
            >
              <MessageCircle className="w-4 h-4 md:w-5 md:h-5" />{t("landing.write_host")}
            </MagneticButton>
          </div>
        </motion.div>

        {/* ═══ Other Properties — infinite marquee carousel (desktop) + swipeable (mobile) ═══
            Visible only when host owns ≥1 other published property. The desktop variant
            auto-scrolls continuously and pauses on hover. The mobile variant relies on
            native horizontal scroll/swipe with snap points — feels nicer than a forced
            auto-scroll on small viewports. Both share the same card design. */}
        {otherProperties.length > 0 && (
          <>
            <Divider theme={theme} thick />
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={9} data-testid="other-properties-section">
              <p className="text-center text-xs font-semibold uppercase tracking-[0.15em] mb-2" style={{ color: theme.sub }}>{t("landing.other_properties")}</p>
              <p className="text-center text-base md:text-lg font-bold mb-6 md:mb-8" style={{ color: theme.text }}>
                Scopri le altre case dello stesso host
              </p>

              {/* Mobile: native horizontal scroll with snap */}
              <div ref={carouselRef} className="md:hidden flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory -mx-4 px-4" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {otherProperties.map((op) => {
                  const slug = op.custom_slug;
                  const target = slug ? `/p/${slug}` : `/property/${op.property_id}`;
                  const city = op.comune?.nome || op.address;
                  return (
                    <div
                      key={op.property_id}
                      onClick={() => { window.scrollTo(0, 0); navigate(target); }}
                      className="shrink-0 w-[78%] max-w-[280px] snap-start cursor-pointer rounded-2xl overflow-hidden bg-white shadow-md active:scale-[0.98] transition-transform"
                      style={{ border: `1px solid ${theme.border}` }}
                      data-testid={`other-prop-${op.property_id}`}
                    >
                      <div className="aspect-[4/3]" style={{ background: theme.border }}>
                        {op.media?.[0]
                          ? <img src={`${API}/files/${op.media[0]}`} alt={op.name} className="w-full h-full object-cover" loading="lazy" />
                          : <div className="w-full h-full flex items-center justify-center"><Home className="w-7 h-7 text-zinc-200" /></div>}
                      </div>
                      <div className="p-3.5">
                        <p className="font-bold text-sm truncate" style={{ color: theme.text }}>{op.name}</p>
                        {city && (
                          <p className="text-[11px] truncate mt-1 flex items-center gap-1" style={{ color: theme.sub }}>
                            <MapPin className="w-3 h-3 shrink-0" />{city}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: infinite marquee — duplicates the list so translateX(-50%)
                  loops seamlessly. Pauses on hover via :hover pseudo-class. */}
              <div className="hidden md:block group relative overflow-hidden -mx-4">
                {/* Edge fade masks to suggest "more out of view" */}
                <div className="absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none" style={{ background: `linear-gradient(to right, ${theme.bg}, transparent)` }} />
                <div className="absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none" style={{ background: `linear-gradient(to left, ${theme.bg}, transparent)` }} />

                <div
                  className="flex gap-5 w-max marquee-track"
                  style={{
                    // Speed scales with the number of cards (~5s per card) so a host
                    // with 20 sister properties still gives a smooth, readable scroll.
                    animation: `marquee-x ${Math.max(20, otherProperties.length * 5)}s linear infinite`,
                  }}
                  data-testid="other-properties-marquee"
                >
                  {[...otherProperties, ...otherProperties].map((op, idx) => {
                    const slug = op.custom_slug;
                    const target = slug ? `/p/${slug}` : `/property/${op.property_id}`;
                    const city = op.comune?.nome || op.address;
                    return (
                      <button
                        key={`${op.property_id}-${idx}`}
                        onClick={() => { window.scrollTo(0, 0); navigate(target); }}
                        className="shrink-0 w-[280px] cursor-pointer rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-xl hover:scale-[1.03] hover:-translate-y-1 transition-all text-left"
                        style={{ border: `1px solid ${theme.border}` }}
                        data-testid={idx < otherProperties.length ? `other-prop-desktop-${op.property_id}` : undefined}
                        aria-hidden={idx >= otherProperties.length}
                      >
                        <div className="aspect-[4/3] overflow-hidden" style={{ background: theme.border }}>
                          {op.media?.[0]
                            ? <img src={`${API}/files/${op.media[0]}`} alt={op.name} className="w-full h-full object-cover transition-transform group-hover:scale-100" loading="lazy" />
                            : <div className="w-full h-full flex items-center justify-center"><Home className="w-8 h-8 text-zinc-200" /></div>}
                        </div>
                        <div className="p-4">
                          <p className="font-bold text-base truncate" style={{ color: theme.text }}>{op.name}</p>
                          {city && (
                            <p className="text-xs truncate mt-1 flex items-center gap-1" style={{ color: theme.sub }}>
                              <MapPin className="w-3 h-3 shrink-0" />{city}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            FOOTER — Informazioni legali custom + Badge HostLand
            1) Footer legale personalizzato (se l'host ha compilato i campi)
            2) Badge "Creato con HostLand" — opt-out via property.footer_show_hostland
            ═══════════════════════════════════════════════════════════════ */}
        <footer className="mt-12 md:mt-16 pb-4" data-testid="property-footer">
          {/* Footer legale custom — discreto, elegante, centrato */}
          {(property.legal_business_name || property.legal_vat_number || property.legal_address || property.legal_copyright || property.legal_cir) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl px-5 py-5 md:px-8 md:py-6 text-center max-w-2xl mx-auto"
              style={{ background: theme.bg === '#fff' ? '#FAFAFB' : 'rgba(255,255,255,0.04)', border: `1px solid ${theme.border}` }}
              data-testid="legal-footer"
            >
              {property.legal_business_name && (
                <p className="text-sm md:text-base font-bold tracking-tight" style={{ color: theme.text }}>
                  {property.legal_business_name}
                </p>
              )}
              {property.legal_address && (
                <p className="text-xs mt-1.5" style={{ color: theme.sub }}>
                  {property.legal_address}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-2">
                {property.legal_vat_number && (
                  <p className="text-[11px] font-medium" style={{ color: theme.sub }}>
                    P.IVA <span className="font-semibold">{property.legal_vat_number}</span>
                  </p>
                )}
                {property.legal_cir && (
                  <p className="text-[11px] font-medium" style={{ color: theme.sub }}>
                    CIR <span className="font-semibold">{property.legal_cir}</span>
                  </p>
                )}
                {property.cin && (
                  <p className="text-[11px] font-medium" style={{ color: theme.sub }}>
                    CIN <span className="font-semibold">{property.cin}</span>
                  </p>
                )}
              </div>
              {property.legal_copyright && (
                <p className="text-[10px] mt-3 italic" style={{ color: theme.sub, opacity: 0.8 }}>
                  {property.legal_copyright}
                </p>
              )}
            </motion.div>
          )}

          {/* Badge HostLand — elegante, piccolo, non invasivo.
              Opt-out tramite property.footer_show_hostland (default: true).
              Punta alla home di Hostland per supportare la crescita organica. */}
          {property.footer_show_hostland !== false && (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 text-center"
            >
              <motion.a
                href={`${window.location.origin}/?utm_source=property_landing&utm_medium=footer_badge&utm_campaign=host_referral`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.04, y: -1 }}
                whileTap={{ scale: 0.97 }}
                className="group inline-flex items-center gap-2 px-3.5 py-2 rounded-full transition-all"
                style={{
                  background: theme.bg === '#fff' ? '#FFFFFF' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${theme.border}`,
                  boxShadow: "0 4px 14px -6px rgba(0,0,0,0.08)"
                }}
                data-testid="hostland-badge"
              >
                <img src="/hostland-logo.png" alt="HostLand" className="w-5 h-5 rounded-[3px] object-cover" />
                <span className="text-[11px] font-medium tracking-tight" style={{ color: theme.sub }}>
                  {({ it: "Questo sito spaziale è stato creato con", en: "This stellar site was created with", fr: "Ce site a été créé avec", es: "Este sitio fue creado con", de: "Diese Seite wurde erstellt mit" }[lang] || "Questo sito spaziale è stato creato con")} <span className="font-bold" style={{ color: theme.text }}>Hostland</span>
                </span>
                <motion.span
                  animate={{ x: [0, 2, 0] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ExternalLink className="w-3 h-3" style={{ color: theme.sub }} />
                </motion.span>
              </motion.a>
            </motion.div>
          )}

          {/* Segnala contenuto (DSA) — link discreto, sotto a tutto */}
          <div className="mt-5 text-center">
            <button
              onClick={() => setShowReport(true)}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium opacity-70 hover:opacity-100 transition-opacity"
              style={{ color: theme.sub }}
              data-testid="report-content-btn"
            >
              <Flag className="w-3 h-3" /> Segnala contenuto
            </button>
          </div>
        </footer>
      </main>

      {/* Modal segnalazione contenuto */}
      <AnimatePresence>
        {showReport && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
            onClick={() => setShowReport(false)} data-testid="report-modal">
            <motion.div initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
              className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl p-6"
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-bold text-zinc-900">Segnala contenuto</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Aiutaci a mantenere Hostland sicuro. Esamineremo ogni segnalazione.</p>
                </div>
                <button onClick={() => setShowReport(false)} className="p-1" data-testid="report-close"><X className="w-4 h-4 text-zinc-400" /></button>
              </div>
              <div className="space-y-2 mb-4">
                {[
                  ["illecito", "Contenuto illecito / illegale"],
                  ["offensivo", "Offensivo o inappropriato"],
                  ["diritti_autore", "Viola diritti d'autore / immagini altrui"],
                  ["privacy", "Viola la privacy"],
                  ["spam_truffa", "Spam o truffa"],
                  ["altro", "Altro"],
                ].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-2.5 text-sm text-zinc-700 cursor-pointer" data-testid={`report-reason-${v}`}>
                    <input type="radio" name="report-reason" value={v} checked={reportReason === v} onChange={() => setReportReason(v)} />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
              <textarea value={reportNote} onChange={(e) => setReportNote(e.target.value)} rows={3} maxLength={1000}
                placeholder="Dettagli (facoltativo)" className="w-full border border-zinc-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-zinc-400 mb-4"
                data-testid="report-note" />
              <button onClick={submitReport} disabled={reportSending}
                className="w-full rounded-xl py-3 font-bold text-sm text-white disabled:opacity-60"
                style={{ background: "#D7362B" }} data-testid="report-submit">
                {reportSending ? "Invio…" : "Invia segnalazione"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Registration Banner */}
      <AnimatePresence>
        {showRegBanner && !user && (
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            className="fixed bottom-20 md:bottom-4 left-4 right-4 z-[60] max-w-md mx-auto" data-testid="register-banner">
            <div className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-xl border" style={{ borderColor: theme.border }}>
              <UserPlus className="w-5 h-5 shrink-0" style={{ color: theme.sub }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: theme.text }}>{t("landing.register_free")}</p>
                <p className="text-[11px]" style={{ color: theme.sub }}>{t("pl.save_loyalty")}</p>
              </div>
              <button onClick={() => navigate("/login")} className="rounded-lg px-3 py-1.5 font-semibold text-[11px]" style={{ background: theme.accent, color: theme.bg }} data-testid="register-banner-btn">{t("landing.signup")}</button>
              <button onClick={() => setShowRegBanner(false)} className="p-1"><X className="w-3.5 h-3.5" style={{ color: theme.sub }} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Host Profile Modal */}
      <AnimatePresence>
        {showHostProfile && host && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setShowHostProfile(false)}>
            <motion.div initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
              className="bg-white p-6 w-full max-w-md max-h-[80vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()} data-testid="host-profile-modal">
              <div className="flex justify-between items-start mb-5">
                <div className="flex items-center gap-3">
                  {host.avatar_url ? (
                    <img src={host.avatar_url.startsWith('http') ? host.avatar_url : `${API}/files/${host.avatar_url}`} alt={host.name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center"><span className="text-lg font-semibold text-zinc-400">{host.name?.charAt(0)}</span></div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-zinc-900">{host.name}</h3>
                    {host.created_at && (
                      <p className="text-xs text-zinc-500 mt-0.5" data-testid="host-seniority-modal">
                        {formatHostSeniority(host.created_at, t)}
                      </p>
                    )}
                  </div>
                </div>
                <button onClick={() => setShowHostProfile(false)} className="p-1.5 rounded-full hover:bg-zinc-100"><X className="w-5 h-5 text-zinc-400" /></button>
              </div>
              {host.bio && <p className="text-sm text-zinc-500 leading-relaxed mb-5">{host.bio}</p>}
              {host.social_links && Object.keys(host.social_links).length > 0 && (
                <div className="flex gap-2">
                  {host.social_links.instagram && <a href={host.social_links.instagram} target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-lg bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors"><Instagram className="w-4 h-4" /></a>}
                  {host.social_links.facebook && <a href={host.social_links.facebook} target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-lg bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors"><Facebook className="w-4 h-4" /></a>}
                  {host.social_links.linkedin && <a href={host.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-lg bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors"><Linkedin className="w-4 h-4" /></a>}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ PWA INSTALL MODAL (pivot 2026-10) — la web app installabile è la
           PROPERTY PAGE. Bottom-sheet nero elegante: ?installa=1 → 10s, altri
           visitatori → 20s, 1 volta per dispositivo, mai se già installata o
           sopra un altro modale. Dopo l'install chiede le notifiche push.
           Saltato nell'anteprima admin (iframe / ?preview=1). ═══ */}
      {property?.property_id && !(() => {
        try {
          const q = new URLSearchParams(window.location.search);
          return q.get("preview") === "1" || q.get("embed") === "1" || q.get("nocookie") === "1" || window.self !== window.top;
        } catch { return true; }
      })() && (
        <PropertyInstallModal
          propertyId={property.property_id}
          name={property.pwa_config?.name || property.name || "l'app"}
          iconUrl={(() => {
            const ic = property.pwa_config?.icon_512 || property.pwa_config?.icon_192 || "/hostland-logo.png";
            return ic.startsWith("http") ? ic : `${BACKEND_URL}${ic}`;
          })()}
          brandColor={property.pwa_config?.theme_color || theme?.accent || "#FF385C"}
          lang={lang}
        />
      )}

      {/* ═══ MOBILE STICKY FOOTER — full-width red bar with rounded top ═══
           Design: replaces the previous floating red pill. Feels like a proper
           app footer that hugs the viewport edge. Each button "lifts" on tap
           (framer-motion whileTap) to hint that the action opens a follow-up
           surface (contact / share). Safe-area bottom padding keeps the
           buttons above the iOS home indicator. */}
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 24, delay: 0.4 }}
        className="md:hidden fixed left-0 right-0 z-30 pointer-events-none"
        // Position by anchoring the BOTTOM edge to the safe-area inset,
        // not padding — this way the bar sits *above* iOS Safari's bottom
        // URL bar even when the URL bar is docked to the bottom (the user
        // preference that made a huge white gap appear in the previous
        // build). `bottom: 0` fallback if the browser doesn't support env().
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
        }}
        data-testid="mobile-sticky-bottom"
      >
        <div
          className="pointer-events-auto mx-3 rounded-[30px] px-3 py-2.5"
          style={{
            background: "linear-gradient(135deg, rgba(215,54,43,0.90) 0%, rgba(184,36,27,0.88) 100%)",
            backdropFilter: "blur(22px) saturate(1.7)",
            WebkitBackdropFilter: "blur(22px) saturate(1.7)",
            border: "1px solid rgba(255,255,255,0.40)",
            boxShadow: "0 16px 40px -10px rgba(215,54,43,0.60), inset 0 1px 0 rgba(255,255,255,0.28)",
          }}
        >
          <div className="flex items-center gap-2">
            {/* Lingua — bandiera, prima del cuore */}
            <LangDropdownGlass />

            {/* Heart — favourite toggle */}
            <motion.button
              whileTap={{ scale: 0.88, y: -4 }}
              transition={{ type: "spring", stiffness: 420, damping: 18 }}
              onClick={handleToggleLike}
              className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center"
              style={{
                background: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.28)",
                WebkitTapHighlightColor: "transparent",
              }}
              data-testid="mobile-save-btn"
              aria-label={liked ? "Rimuovi dai preferiti" : "Salva nei preferiti"}
            >
              <motion.span
                animate={liked ? { scale: [1, 1.35, 1] } : { scale: 1 }}
                transition={{ duration: 0.35 }}
                style={{ display: "inline-flex" }}
              >
                <Heart
                  className="w-5 h-5 transition-colors"
                  style={{ color: "#fff", fill: liked ? "#fff" : "none" }}
                  strokeWidth={2.4}
                />
              </motion.span>
            </motion.button>

            {/* Share — opens the share sheet with a lift animation */}
            <motion.button
              whileTap={{ scale: 0.88, y: -4 }}
              transition={{ type: "spring", stiffness: 420, damping: 18 }}
              onClick={handleShare}
              className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center"
              style={{
                background: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.28)",
                WebkitTapHighlightColor: "transparent",
              }}
              data-testid="mobile-share-btn"
              aria-label="Condividi"
            >
              {shareCopied
                ? <Check className="w-5 h-5 text-white" strokeWidth={2.6} />
                : <Share2 className="w-5 h-5 text-white" strokeWidth={2.4} />}
            </motion.button>

            {/* Contact — primary CTA, lifts with a spring on tap to hint the
                 modal that opens below. */}
            <motion.button
              whileTap={{ scale: 0.97, y: -6 }}
              transition={{ type: "spring", stiffness: 420, damping: 18 }}
              onClick={() => setShowContactPopup(true)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 px-3 rounded-2xl text-[14px] font-black tracking-tight whitespace-nowrap"
              style={{
                background: "#FFFFFF",
                color: "#D7362B",
                WebkitTapHighlightColor: "transparent",
                boxShadow: "0 6px 18px -6px rgba(0,0,0,0.25)",
              }}
              data-testid="mobile-contact-btn"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2.6} />
              {t("pl.contact_host")}
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* ═══ PHOTOS LIGHTBOX MODAL ═══ */}
      <AnimatePresence>
        {showAllPhotos && property.media && property.media.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black overflow-y-auto"
            data-testid="photos-lightbox"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-black/85 backdrop-blur-md">
              <button
                onClick={() => setShowAllPhotos(false)}
                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                data-testid="lightbox-close-btn"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <p className="text-sm font-semibold text-white">{property.media.length} foto</p>
              <div className="w-10" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-6 space-y-3 pb-20">
              {property.media.map((m, i) => (
                <div key={i} className="rounded-xl overflow-hidden">
                  <img src={resolveMediaUrl(m)} alt={`Foto ${i + 1}`} className="w-full h-auto" />
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ HOST DIRECT CONTACT POPUP — Phone / Email / WhatsApp ═══ */}
      <ContactPopup
        open={showContactPopup}
        onClose={() => setShowContactPopup(false)}
        property={property}
        host={host}
        t={t}
      />

      {/* ═══ SHARE POPUP — Copy link / socials ═══ */}
      <SharePopup
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        property={property}
        theme={theme}
      />

      {/* ═══ MAP MODAL — full-screen interactive map without leaving the page ═══
          The old "Espandi mappa" opened OpenStreetMap in a new tab, which broke
          the app-like feel and made it hard to come back. Now it opens a modal
          in-place with a close X in the top-right. Backdrop click also closes. */}
      <AnimatePresence>
        {mapModalOpen && property?.location_lat && property?.location_lng && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMapModalOpen(false)}
              className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm"
              data-testid="map-modal-backdrop"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ type: "spring", stiffness: 240, damping: 26 }}
              className="fixed inset-4 md:inset-8 lg:inset-16 z-[96] rounded-3xl overflow-hidden bg-white shadow-2xl flex flex-col"
              data-testid="map-modal"
            >
              <button
                type="button"
                onClick={() => setMapModalOpen(false)}
                className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-zinc-50 transition-colors"
                aria-label="Chiudi mappa"
                data-testid="map-modal-close"
              >
                <X className="w-5 h-5 text-zinc-700" strokeWidth={2.4} />
              </button>
              {property.address && (
                <div className="absolute top-3 left-3 z-10 px-3 py-2 rounded-full bg-white/95 backdrop-blur shadow-lg max-w-[70%]">
                  <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Dove ti troverai</p>
                  <p className="text-[13px] font-bold text-zinc-900 truncate">{property.address}</p>
                </div>
              )}
              <iframe
                title="Mappa interattiva"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${property.location_lng - 0.015},${property.location_lat - 0.010},${property.location_lng + 0.015},${property.location_lat + 0.010}&layer=mapnik&marker=${property.location_lat},${property.location_lng}`}
                className="flex-1 w-full"
                style={{ border: 0 }}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PropertyLanding;
